import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { getAllServiceLinks } from '../data/kmsServices';
import { 
  Home, LogOut, RefreshCw, CheckCircle, XCircle, 
  TrendingUp, Database, Clock, AlertCircle, Sparkles,
  Eye, Bot, Calendar, Globe, Users, MapPin, DollarSign, ArrowRight,
  HelpCircle, Phone, Mail, MessageSquare, CreditCard, Search, FileText,
  Crown, Lock, Unlock, Edit2, Newspaper, Loader2, Trash2, ChevronDown, ChevronUp, X,
  Smartphone, Bell, Send, Activity, Download, PieChart, Zap, Briefcase, Cpu
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [recentAdditions, setRecentAdditions] = useState([]);
  const [loginStats, setLoginStats] = useState(null);
  const [districtData, setDistrictData] = useState([]);
  const [casteData, setCasteData] = useState([]);
  const [incomeData, setIncomeData] = useState([]);
  const [recentLogins, setRecentLogins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [helpRequests, setHelpRequests] = useState([]);
  const [helpStats, setHelpStats] = useState(null);
  const [allSchemes, setAllSchemes] = useState([]);
  const [schemeSearch, setSchemeSearch] = useState('');
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // News management state
  const [newsMetadata, setNewsMetadata] = useState(null);
  const [newsRefreshing, setNewsRefreshing] = useState(false);
  const [newsMessage, setNewsMessage] = useState(null);
  // Tech news state
  const [techNewsCount, setTechNewsCount] = useState(null);
  const [techNewsLastUpdated, setTechNewsLastUpdated] = useState(null);
  const [techRefreshing, setTechRefreshing] = useState(false);
  const [techMessage, setTechMessage] = useState(null);
  
  // Scheme expansion and delete state
  const [expandedScheme, setExpandedScheme] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Mobile App Management State
  const [mobileStats, setMobileStats] = useState(null);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationPlatform, setNotificationPlatform] = useState('all');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [personalizedRunning, setPersonalizedRunning] = useState(false);
  const [personalizedResult, setPersonalizedResult] = useState(null);
  const [linkHealthRunning, setLinkHealthRunning] = useState(false);
  const [linkHealthReport, setLinkHealthReport] = useState(null);
  
  // View Reset Management State
  const [viewResetData, setViewResetData] = useState(null);

  // Training-data consent history modal
  const [consentHistoryOpen, setConsentHistoryOpen] = useState(false);
  const [consentHistoryLoading, setConsentHistoryLoading] = useState(false);
  const [consentHistoryUser, setConsentHistoryUser] = useState(null);
  const [consentHistoryItems, setConsentHistoryItems] = useState([]);

  const openConsentHistory = async (targetUser) => {
    setConsentHistoryUser(targetUser);
    setConsentHistoryOpen(true);
    setConsentHistoryLoading(true);
    setConsentHistoryItems([]);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/api/auth/admin/users/${targetUser.id}/consent-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConsentHistoryItems(res.data.history || []);
    } catch (err) {
      console.error('Error loading consent history:', err);
    } finally {
      setConsentHistoryLoading(false);
    }
  };

  // Discovery Queue State
  const [discoveryStats, setDiscoveryStats] = useState(null);
  const [discoveredSchemes, setDiscoveredSchemes] = useState([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [resettingViews, setResettingViews] = useState(false);

  // KMS Training Data State
  const [occupationStats, setOccupationStats] = useState(null);
  const [trainingEntries, setTrainingEntries] = useState([]);
  const [trainingTotal, setTrainingTotal] = useState(0);
  const [trainingPage, setTrainingPage] = useState(0);
  const [trainingHasMore, setTrainingHasMore] = useState(false);
  const [trainingTab, setTrainingTab] = useState('overview');
  const [trainingFilters, setTrainingFilters] = useState({ occupation: '', platform: '', language: '', location: '', date_from: '', date_to: '' });
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');

  // Government Jobs Management State
  const [jobsStats, setJobsStats] = useState(null);
  const [adminJobs, setAdminJobs] = useState([]);
  const [adminJobsTotal, setAdminJobsTotal] = useState(0);
  const [adminJobsPage, setAdminJobsPage] = useState(1);
  const [adminJobsSearch, setAdminJobsSearch] = useState('');
  const [jobsRefreshing, setJobsRefreshing] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [newJob, setNewJob] = useState({ title: '', department: '', organisation: '', category: 'Central', qualification: 'Various', location: 'All India', vacancies: '', salary_min: '', salary_max: '', last_date: '', apply_url: '', notification_url: '' });

  // Money Hub admin state
  const [moneyHubLoading, setMoneyHubLoading] = useState({});
  const [moneyHubResults, setMoneyHubResults] = useState({});

  const refreshMoneyHubCollection = async (collection) => {
    if (!window.confirm(`Refresh "${collection}"? This will ask Claude to pull the latest rates and overwrite the current data.`)) return;
    setMoneyHubLoading((s) => ({ ...s, [collection]: true }));
    setMoneyHubResults((s) => ({ ...s, [collection]: null }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/money-hub/refresh`,
        { collection },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMoneyHubResults((s) => ({ ...s, [collection]: { ok: true, rows: res.data.rows } }));
      setSuccess(`Refreshed "${collection}" — ${res.data.rows} rows.`);
    } catch (err) {
      setMoneyHubResults((s) => ({ ...s, [collection]: { ok: false, error: err.response?.data?.detail || err.message } }));
      setError(`Refresh failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setMoneyHubLoading((s) => ({ ...s, [collection]: false }));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    loadStats();
    loadRecentAdditions();
    loadLoginStats();
    loadDistrictData();
    loadCasteData();
    loadIncomeData();
    loadRecentLogins();
    loadAllUsers();
    loadHelpRequests();
    loadAllSchemes();
    loadPayments();
    loadNewsMetadata();
    loadTechNewsStats();
    loadMobileStats();
    loadLinkHealth();
    loadViewResetData();
    loadDiscoveryData();
    loadOccupationStats();
    loadJobsData();
  }, []);
  
  // Load Mobile Stats
  const loadMobileStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mobile/stats`);
      setMobileStats(response.data);
      setNotificationHistory(response.data.notifications?.recent || []);
    } catch (error) {
      console.error('Error loading mobile stats:', error);
    }
  };
  
  // Partner link health check — gathers the whole For You catalogue and checks each URL
  const loadLinkHealth = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/link-health/latest`);
      if (res.data && res.data.summary) setLinkHealthReport(res.data);
    } catch (e) { /* no report yet */ }
  };

  const handleRunLinkHealth = async () => {
    setLinkHealthRunning(true);
    const prev = linkHealthReport?.created_at || null;
    try {
      const links = getAllServiceLinks();
      await axios.post(`${API_URL}/api/admin/link-health/run`, { links });
      // Check runs in the background (too many links for one request) — poll until a new report lands.
      const start = Date.now();
      const poll = async () => {
        try {
          const res = await axios.get(`${API_URL}/api/admin/link-health/latest`);
          const cur = res.data?.created_at || null;
          if (cur && cur !== prev) {
            setLinkHealthReport(res.data);
            setLinkHealthRunning(false);
            return;
          }
        } catch (e) { /* keep polling */ }
        if (Date.now() - start > 210000) { setLinkHealthRunning(false); return; }  // 3.5min safety
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 4000);
    } catch (err) {
      setError(`Link health check failed: ${err.response?.data?.detail || err.message}`);
      setLinkHealthRunning(false);
    }
  };

  // Run personalized notification sweep (per-user, auto content)
  const handleRunPersonalized = async () => {
    setPersonalizedRunning(true);
    setPersonalizedResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/mobile/send-personalized-notifications`);
      setPersonalizedResult(res.data?.stats || {});
      loadMobileStats();
    } catch (err) {
      setError(`Failed to run personalized notifications: ${err.response?.data?.detail || err.message}`);
    } finally {
      setPersonalizedRunning(false);
    }
  };

  // Send Push Notification
  const handleSendNotification = async () => {    if (!notificationTitle.trim() || !notificationBody.trim()) {
      setError('Please enter both title and message');
      return;
    }
    
    setSendingNotification(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${API_URL}/api/mobile/send-notification`, {
        title: notificationTitle,
        body: notificationBody,
        topic: 'all',
        target_platform: notificationPlatform
      });
      
      const { success_count = 0, failure_count = 0, errors = [], tokens_deactivated = 0 } = response.data;
      const platformLabel = notificationPlatform === 'all' ? 'all platforms' : notificationPlatform;
      
      if (failure_count > 0 && success_count === 0) {
        // Total failure
        const deactivatedMsg = tokens_deactivated > 0 ? ` (${tokens_deactivated} invalid tokens auto-deactivated)` : '';
        const errorDetails = errors.length > 0 ? errors.join(' | ') : '';
        const isTokenIssue = errorDetails.toLowerCase().includes('invalid token');
        setError(
          `Failed to deliver to any ${platformLabel} device. ${failure_count} failed.${deactivatedMsg}\n${errorDetails}` +
          (isTokenIssue ? '\n\nFix: The iOS app is sending APNs tokens instead of FCM tokens. In your React Native app, use messaging().getToken() (not getAPNSToken()). Users need to update the app to re-register with valid FCM tokens.' : '')
        );
      } else if (failure_count > 0) {
        // Partial success
        setSuccess(`Partially sent to ${platformLabel}: ${success_count} reached, ${failure_count} failed.`);
        if (errors.length > 0) {
          setError(`Errors: ${errors.join(' | ')}`);
        }
      } else if (success_count > 0) {
        // Full success
        setSuccess(`Notification sent to ${platformLabel}! ${success_count} device(s) reached.`);
      } else {
        setError(errors.length > 0 ? errors.join(' | ') : `No active ${platformLabel} devices found.`);
      }
      setNotificationTitle('');
      setNotificationBody('');
      loadMobileStats(); // Refresh stats
    } catch (error) {
      setError(`Failed to send notification: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingNotification(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/automation/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRecentAdditions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/automation/recent-additions`);
      setRecentAdditions(response.data.updates || response.data.schemes || []);
    } catch (error) {
      console.error('Error loading recent additions:', error);
    }
  };

  const fetchSchemeNews = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await axios.post(`${API_URL}/api/automation/fetch-scheme-news`);
      setSuccess('Successfully fetched latest scheme news!');
      loadRecentAdditions(); // Reload the data
    } catch (error) {
      setError(`Failed to fetch news: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadLoginStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/login-stats`);
      setLoginStats(response.data);
    } catch (error) {
      console.error('Error loading login stats:', error);
    }
  };

  const loadDistrictData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/district-distribution`);
      setDistrictData(response.data.districts);
    } catch (error) {
      console.error('Error loading district data:', error);
    }
  };

  const loadCasteData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/caste-distribution`);
      setCasteData(response.data.castes);
    } catch (error) {
      console.error('Error loading caste data:', error);
    }
  };

  const loadIncomeData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/income-distribution`);
      setIncomeData(response.data.income_brackets);
    } catch (error) {
      console.error('Error loading income data:', error);
    }
  };

  const loadRecentLogins = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/recent-logins?limit=20`);
      setRecentLogins(response.data.recent_logins);
    } catch (error) {
      console.error('Error loading recent logins:', error);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/user-details`);
      setAllUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This will remove all their data including saved schemes, notifications, login history, and subscriptions. This action cannot be undone.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/auth/admin/delete-user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`User "${userName}" deleted successfully.`);
      setAllUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      setError(`Failed to delete user: ${error.response?.data?.detail || error.message}`);
    }
  };

  const loadHelpRequests = async () => {
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/help-requests`),
        axios.get(`${API_URL}/api/help-requests/stats`)
      ]);
      setHelpRequests(requestsRes.data);
      setHelpStats(statsRes.data);
    } catch (error) {
      console.error('Error loading help requests:', error);
    }
  };

  const loadAllSchemes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schemes`);
      setAllSchemes(response.data);
    } catch (error) {
      console.error('Error loading all schemes:', error);
    }
  };

  const loadPayments = async () => {
    try {
      const [paymentsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscription/admin/payments`),
        axios.get(`${API_URL}/api/subscription/admin/stats`)
      ]);
      setPayments(paymentsRes.data.payments || []);
      setPaymentStats(statsRes.data);
    } catch (error) {
      console.error('Error loading payments:', error);
    }
  };

  // Load news metadata
  const loadNewsMetadata = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news-v2/metadata`);
      setNewsMetadata(response.data);
    } catch (error) {
      console.error('Error loading news metadata:', error);
    }
  };

  // Refresh news (admin only)
  const handleRefreshNews = async () => {
    if (newsRefreshing) return;
    
    setNewsRefreshing(true);
    setNewsMessage(null);
    
    try {
      // Trigger backend refresh
      await axios.post(`${API_URL}/api/news-v2/refresh`);
      
      setNewsMessage({
        type: 'info',
        text: 'News refresh started. This may take a few minutes...'
      });
      
      // Poll for completion (check every 10 seconds for up to 3 minutes)
      let attempts = 0;
      const maxAttempts = 18;
      
      const checkRefresh = async () => {
        attempts++;
        
        try {
          const response = await axios.get(`${API_URL}/api/news-v2/metadata`);
          const newMetadata = response.data;
          
          // Check if refresh completed (hours_ago should be very small)
          if (newMetadata.hours_ago < 0.1) {
            setNewsMetadata(newMetadata);
            setNewsMessage({
              type: 'success',
              text: `News refreshed successfully! ${newMetadata.news_count} articles loaded.`
            });
            setNewsRefreshing(false);
            return;
          }
          
          if (attempts < maxAttempts) {
            setTimeout(checkRefresh, 10000);
          } else {
            // Timeout - refresh might still be running
            setNewsMetadata(newMetadata);
            setNewsMessage({
              type: 'info',
              text: 'Refresh is still processing. Check back in a moment.'
            });
            setNewsRefreshing(false);
          }
        } catch (err) {
          if (attempts < maxAttempts) {
            setTimeout(checkRefresh, 10000);
          } else {
            setNewsMessage({
              type: 'error',
              text: 'Could not verify refresh status.'
            });
            setNewsRefreshing(false);
          }
        }
      };
      
      // Start checking after 5 seconds
      setTimeout(checkRefresh, 5000);
      
    } catch (error) {
      console.error('Error refreshing news:', error);
      setNewsMessage({
        type: 'error',
        text: 'Failed to start news refresh. Please try again.'
      });
      setNewsRefreshing(false);
    }
  };

  // Load tech-news stats (count + last updated)
  const loadTechNewsStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news-v2/tech?limit=200`);
      setTechNewsCount(response.data.total_count ?? null);
      setTechNewsLastUpdated(response.data.last_updated ?? null);
    } catch (error) {
      console.error('Error loading tech-news stats:', error);
    }
  };

  // Refresh tech news (runs in background on the server)
  const handleRefreshTechNews = async () => {
    if (techRefreshing) return;
    setTechRefreshing(true);
    setTechMessage(null);
    try {
      const res = await axios.post(`${API_URL}/api/news-v2/tech/refresh`);
      setTechMessage({
        type: 'info',
        text: res.data?.message || 'Tech-news refresh started. New articles appear in 2-3 minutes.',
      });
      // Poll the count for up to ~3 minutes to confirm new items landed
      let attempts = 0;
      const startCount = techNewsCount || 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API_URL}/api/news-v2/tech?limit=200`);
          const newCount = r.data.total_count ?? 0;
          setTechNewsCount(newCount);
          setTechNewsLastUpdated(r.data.last_updated ?? null);
          if (newCount > startCount) {
            setTechMessage({ type: 'success', text: `Tech News refreshed — ${newCount} articles now live.` });
            setTechRefreshing(false);
            return;
          }
        } catch (e) {
          console.error('tech poll error', e);
        }
        if (attempts < 18) {
          setTimeout(poll, 10000);
        } else {
          setTechMessage({ type: 'info', text: 'Refresh still processing in the background. Check back shortly.' });
          setTechRefreshing(false);
        }
      };
      setTimeout(poll, 15000);
    } catch (error) {
      console.error('Error refreshing tech news:', error);
      setTechMessage({ type: 'error', text: 'Failed to start tech-news refresh. Please try again.' });
      setTechRefreshing(false);
    }
  };

  // Load view reset data
  const loadViewResetData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schemes/admin/view-reset-status`);
      setViewResetData(response.data);
    } catch (error) {
      console.error('Error loading view reset data:', error);
    }
  };

  // Discovery Queue functions
  const loadDiscoveryData = async () => {
    setDiscoveryLoading(true);
    try {
      const [statsRes, schemesRes, sweepRes] = await Promise.all([
        axios.get(`${API_URL}/api/schemes/discovery/stats`),
        axios.get(`${API_URL}/api/schemes/discovery/pending?limit=50`),
        axios.get(`${API_URL}/api/schemes/discovery/sweep-runs?limit=20`),
      ]);
      setDiscoveryStats(statsRes.data);
      setDiscoveredSchemes(schemesRes.data.schemes || []);
      setSweepStatus(sweepRes.data);
    } catch (error) {
      console.error('Error loading discovery data:', error);
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleDiscoveryAction = async (schemeId, action) => {
    try {
      await axios.post(`${API_URL}/api/schemes/discovery/${action}/${schemeId}`);
      setSuccess(`Scheme ${action === 'verify' ? 'verified' : action === 'reject' ? 'rejected' : 'marked as duplicate'} successfully`);
      loadDiscoveryData();
    } catch (error) {
      setError(`Failed to ${action} scheme: ` + (error.response?.data?.detail || error.message));
    }
  };

  // SEO: slug backfill (dry-run + apply)
  const [slugBackfill, setSlugBackfill] = useState({ loading: false, result: null, mode: null });

  // Sweep status / run-now
  const [sweepStatus, setSweepStatus] = useState(null);
  const [sweepRunning, setSweepRunning] = useState(false);
  const [sweepRegion, setSweepRegion] = useState('');  // '' = all 4

  const runSweepNow = async () => {
    if (!window.confirm(`Run discovery sweep${sweepRegion ? ' for ' + sweepRegion : ' for all 4 regions'} now?\n\nThis calls Claude Haiku and may take 30–90 seconds per region. Newly-found schemes go to the pending-review queue.`)) {
      return;
    }
    setSweepRunning(true);
    try {
      const url = `${API_URL}/api/schemes/discovery/sweep${sweepRegion ? `?region=${encodeURIComponent(sweepRegion)}` : ''}`;
      const res = await axios.post(url);
      setSuccess(`Sweep complete: ${res.data.total_new} new schemes across ${res.data.regions.length} region(s).`);
      loadDiscoveryData();
    } catch (err) {
      setError('Sweep failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSweepRunning(false);
    }
  };

  const runSlugBackfill = async (apply) => {
    if (apply && !window.confirm('This will rewrite slugs for every scheme/job with a UUID or underscore slug. Old URLs still resolve via the existing id-or-slug lookup. Continue?')) {
      return;
    }
    setSlugBackfill({ loading: true, result: null, mode: apply ? 'APPLY' : 'DRY_RUN' });
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/admin/seo/backfill-slugs${apply ? '?apply=true' : ''}`;
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSlugBackfill({ loading: false, result: res.data, mode: apply ? 'APPLY' : 'DRY_RUN' });
      if (apply) setSuccess('Slug backfill applied successfully. Remember to purge Cloudflare cache for /api/sitemap*.xml.');
    } catch (err) {
      setError('Slug backfill failed: ' + (err.response?.data?.detail || err.message));
      setSlugBackfill({ loading: false, result: null, mode: null });
    }
  };

  const loadOccupationStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/impact-analyser/occupation-stats`);
      setOccupationStats(response.data);
    } catch (error) {
      console.error('Error loading occupation stats:', error);
    }
  };

  // Government Jobs admin functions
  const loadJobsData = async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        axios.get(`${API_URL}/api/jobs/admin/stats`),
        axios.get(`${API_URL}/api/jobs/admin/all?page=1&limit=50`)
      ]);
      setJobsStats(statsRes.data);
      setAdminJobs(jobsRes.data.jobs || []);
      setAdminJobsTotal(jobsRes.data.total || 0);
    } catch (error) {
      console.error('Error loading jobs data:', error);
    }
  };

  const loadAdminJobs = async (pg = 1, search = '') => {
    setJobsLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 50 });
      if (search) params.append('search', search);
      const res = await axios.get(`${API_URL}/api/jobs/admin/all?${params}`);
      setAdminJobs(res.data.jobs || []);
      setAdminJobsTotal(res.data.total || 0);
      setAdminJobsPage(pg);
    } catch (error) {
      console.error('Error loading admin jobs:', error);
    } finally {
      setJobsLoading(false);
    }
  };

  const handleRefreshJobs = async () => {
    setJobsRefreshing(true);
    setSuccess('');
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/jobs/admin/refresh`);
      setSuccess(`Refresh started in background! Currently ${res.data.total_active_before} active jobs. New jobs will appear in 5-10 minutes.`);
      // Auto-reload stats after 30 seconds
      setTimeout(() => loadJobsData(), 30000);
    } catch (error) {
      setError('Failed to refresh jobs: ' + (error.response?.data?.detail || error.message));
    } finally {
      setJobsRefreshing(false);
    }
  };

  const handleCreateJob = async () => {
    try {
      const payload = { ...newJob };
      if (payload.vacancies) payload.vacancies = parseInt(payload.vacancies);
      if (payload.salary_min) payload.salary_min = parseInt(payload.salary_min);
      if (payload.salary_max) payload.salary_max = parseInt(payload.salary_max);
      if (!payload.vacancies) delete payload.vacancies;
      if (!payload.salary_min) delete payload.salary_min;
      if (!payload.salary_max) delete payload.salary_max;
      await axios.post(`${API_URL}/api/jobs/admin/create`, payload);
      setSuccess('Job created successfully!');
      setShowAddJob(false);
      setNewJob({ title: '', department: '', organisation: '', category: 'Central', qualification: 'Various', location: 'All India', vacancies: '', salary_min: '', salary_max: '', last_date: '', apply_url: '', notification_url: '' });
      loadJobsData();
    } catch (error) {
      setError('Failed to create job: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await axios.delete(`${API_URL}/api/jobs/admin/${jobId}`);
      setSuccess('Job deleted.');
      loadAdminJobs(adminJobsPage, adminJobsSearch);
      loadJobsData();
    } catch (error) {
      setError('Failed to delete job.');
    }
  };

  const handleUpdateJob = async (jobId, updates) => {
    try {
      await axios.put(`${API_URL}/api/jobs/admin/${jobId}`, updates);
      setSuccess('Job updated.');
      setEditingJob(null);
      loadAdminJobs(adminJobsPage, adminJobsSearch);
    } catch (error) {
      setError('Failed to update job.');
    }
  };

  const loadTrainingEntries = async (page = 0, filters = trainingFilters) => {
    try {
      const params = new URLSearchParams({ skip: page * 25, limit: 25 });
      if (filters.occupation) params.append('occupation', filters.occupation);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.language) params.append('language', filters.language);
      if (filters.location) params.append('location', filters.location);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      const response = await axios.get(`${API_URL}/api/impact-analyser/training-data?${params.toString()}`);
      setTrainingEntries(response.data.entries);
      setTrainingTotal(response.data.total);
      setTrainingHasMore(response.data.has_more);
      setTrainingPage(page);
    } catch (error) {
      console.error('Error loading training entries:', error);
    }
  };

  const handleTrainingFilterChange = (key, value) => {
    const newFilters = { ...trainingFilters, [key]: value };
    setTrainingFilters(newFilters);
    loadTrainingEntries(0, newFilters);
  };

  const handleExportTrainingData = (format) => {
    const params = new URLSearchParams({ format });
    if (exportDateFrom) params.append('date_from', exportDateFrom);
    if (exportDateTo) params.append('date_to', exportDateTo);
    window.open(`${API_URL}/api/impact-analyser/training-data/export?${params.toString()}`, '_blank');
  };

  // Reset views for a specific user
  const resetUserViews = async (userId, userName) => {
    setResettingViews(true);
    try {
      await axios.post(`${API_URL}/api/schemes/admin/reset-user-views/${userId}`);
      setSuccess(`Views reset for ${userName}`);
      loadViewResetData(); // Refresh data
    } catch (error) {
      setError('Failed to reset views: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResettingViews(false);
    }
  };

  // Reset all expired views
  const resetAllExpiredViews = async () => {
    setResettingViews(true);
    try {
      const response = await axios.post(`${API_URL}/api/schemes/admin/reset-all-expired-views`);
      setSuccess(`Reset ${response.data.records_reset} expired view records`);
      loadViewResetData(); // Refresh data
    } catch (error) {
      setError('Failed to reset expired views: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResettingViews(false);
    }
  };

  const updateHelpRequestStatus = async (requestId, newStatus, notes = '') => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/help-requests/${requestId}`, {
        status: newStatus,
        notes: notes
      });
      setSuccess(`Request status updated to ${newStatus}`);
      loadHelpRequests();
    } catch (error) {
      setError('Failed to update request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Update scheme tier (Free <-> Premium)
  const updateSchemeTier = async (schemeId, isPremium, schemeName) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.patch(`${API_URL}/api/schemes/${schemeId}/tier`, {
        is_premium: isPremium
      });
      
      setSuccess(`"${schemeName}" updated to ${isPremium ? 'Premium' : 'Free'} tier`);
      
      // Update local state to reflect the change immediately
      setAllSchemes(prevSchemes => 
        prevSchemes.map(scheme => 
          scheme.id === schemeId 
            ? { ...scheme, is_premium: isPremium, is_locked: isPremium }
            : scheme
        )
      );
    } catch (error) {
      setError('Failed to update scheme tier: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Delete scheme
  const deleteScheme = async (schemeId, schemeName) => {
    setDeleting(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.delete(`${API_URL}/api/schemes/${schemeId}`);
      
      setSuccess(`"${schemeName}" has been deleted successfully`);
      setDeleteConfirm(null);
      
      // Remove from local state immediately
      setAllSchemes(prevSchemes => 
        prevSchemes.filter(scheme => scheme.id !== schemeId)
      );
    } catch (error) {
      setError('Failed to delete scheme: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleting(false);
    }
  };

  const triggerManualScraping = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${API_URL}/api/automation/trigger-scraping`);
      setSuccess(`Scraping triggered! Discovered ${response.data.discoveries} new items.`);
      setTimeout(() => {
        loadStats();
        loadRecentDiscoveries();
      }, 2000);
    } catch (error) {
      setError('Failed to trigger scraping: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card className="p-6 bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-700 mb-1">{title}</p>
          <h3 className={`text-3xl font-bold ${color}`}>{value}</h3>
          {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 ${color.replace('text', 'bg').replace('700', '100')} rounded-lg`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen dark-gradient-bg">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.jpeg" alt="Logo" className="w-12 h-12 rounded-xl shadow-lg object-cover" />
            <div>
              <h1 className="text-2xl font-bold text-green-700">
                Admin Dashboard
              </h1>
              <p className="text-xs text-gray-700">Automated Scheme Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/')} 
              variant="outline" 
              className="border-green-600 text-green-700 hover:bg-green-50"
            >
              <Home className="mr-2 w-4 h-4" />
              Home
            </Button>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2 bg-white p-2 rounded-lg border border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'overview' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-700 hover:bg-green-50'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('add-scheme')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'add-scheme' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-700 hover:bg-green-50'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            Add Scheme
          </button>
          <button
            onClick={() => setActiveTab('customers')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'customers' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-700 hover:bg-green-50'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Customer Analytics
          </button>
          <button
            onClick={() => setActiveTab('all-schemes')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'all-schemes' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-700 hover:bg-green-50'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            All Schemes ({allSchemes.length})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'payments' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-700 hover:bg-green-50'
            }`}
          >
            <CreditCard className="w-4 h-4 inline mr-2" />
            Payments
          </button>
          <button
            onClick={() => setActiveTab('news')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'news' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-700 hover:bg-green-50'
            }`}
          >
            <Newspaper className="w-4 h-4 inline mr-2" />
            News Management
          </button>
          <button
            onClick={() => setActiveTab('mobile')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'mobile' 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-700 hover:bg-purple-50'
            }`}
          >
            <Smartphone className="w-4 h-4 inline mr-2" />
            Mobile App
          </button>
          <button
            onClick={() => setActiveTab('view-reset')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'view-reset' 
                ? 'bg-amber-600 text-white' 
                : 'text-gray-700 hover:bg-amber-50'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            View Reset
          </button>
          <button
            onClick={() => setActiveTab('tab-analytics')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'tab-analytics' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-700 hover:bg-green-50'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Tab Analytics
          </button>
          <button
            onClick={() => setActiveTab('discovery')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'discovery' 
                ? 'bg-orange-600 text-white' 
                : 'text-gray-700 hover:bg-orange-50'
            }`}
            data-testid="tab-discovery"
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Discovery Queue {discoveryStats?.pending_verification > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{discoveryStats.pending_verification}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('govt-jobs')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'govt-jobs' 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-700 hover:bg-blue-50'
            }`}
            data-testid="tab-govt-jobs"
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            Govt Jobs
          </button>
          <button
            onClick={() => setActiveTab('money-hub')}
            className={`px-4 py-2 rounded-md font-medium transition ${
              activeTab === 'money-hub' 
                ? 'bg-emerald-600 text-white' 
                : 'text-gray-700 hover:bg-emerald-50'
            }`}
            data-testid="tab-money-hub"
          >
            <Database className="w-4 h-4 inline mr-2" />
            Money Hub
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard 
                icon={Database}
                title="Total Discovered"
                value={stats.total_discovered}
                color="text-blue-700"
                subtitle="Items found from all sources"
              />
              <StatCard 
                icon={CheckCircle}
                title="Processed"
                value={stats.total_processed}
                color="text-green-700"
                subtitle="AI extracted and analyzed"
              />
              <StatCard 
                icon={Bot}
                title="Auto-Added Schemes"
                value={stats.auto_added_schemes}
                color="text-purple-700"
                subtitle="Successfully added by AI"
              />
            </div>

            {/* Controls */}
            <Card className="p-6 mb-8 bg-white border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Automation Controls</h3>
                  <p className="text-sm text-gray-700">Last scraping: {new Date(stats.last_scraping_time).toLocaleString()}</p>
                  <p className="text-xs text-gray-600 mt-1">Next scheduled: Daily at 2:00 AM</p>
                </div>
                <Button 
                  onClick={triggerManualScraping} 
                  disabled={loading}
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="mr-2 w-4 h-4 animate-spin" />
                      Scraping...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 w-4 h-4" />
                      Trigger Manual Scraping
                    </>
                  )}
                </Button>
              </div>
            </Card>

            {/* KMS Smart Discovery Stats */}
            {discoveryStats && (
              <Card className="p-6 mb-8 bg-white border border-orange-200">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">KMS Smart Auto-Discovery</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">{discoveryStats.total_schemes}</p>
                    <p className="text-xs text-gray-600">Total Schemes</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{discoveryStats.total_discovered}</p>
                    <p className="text-xs text-gray-600">AI Discovered</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{discoveryStats.pending_verification}</p>
                    <p className="text-xs text-gray-600">Pending Review</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{discoveryStats.discovered_this_week}</p>
                    <p className="text-xs text-gray-600">This Week</p>
                  </div>
                </div>
              </Card>
            )}

            {/* System Status */}
            <Card className="p-6 bg-white border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-700" />
                    <span className="font-medium text-gray-900">Automation Scheduler</span>
                  </div>
                  <Badge className="bg-green-600 text-white">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-700" />
                    <span className="font-medium text-gray-900">AI Processor (GPT-4o-mini)</span>
                  </div>
                  <Badge className="bg-green-600 text-white">Running</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-700" />
                    <span className="font-medium text-gray-900">Web Scrapers</span>
                  </div>
                  <Badge className="bg-green-600 text-white">Online</Badge>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Customer Analytics Tab */}
        {activeTab === 'customers' && loginStats && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Login Analytics</h2>
            
            {/* Login Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <StatCard 
                icon={Users}
                title="Total Users"
                value={loginStats.total_users}
                color="text-blue-700"
                subtitle="Registered customers"
              />
              <StatCard 
                icon={TrendingUp}
                title="Total Logins"
                value={loginStats.total_logins}
                color="text-green-700"
                subtitle="All-time logins"
              />
              <StatCard 
                icon={Calendar}
                title="Today"
                value={loginStats.logins_today}
                color="text-purple-700"
                subtitle="Logins today"
              />
              <StatCard 
                icon={Calendar}
                title="This Week"
                value={loginStats.logins_this_week}
                color="text-orange-700"
                subtitle="Last 7 days"
              />
              <StatCard 
                icon={Calendar}
                title="This Month"
                value={loginStats.logins_this_month}
                color="text-pink-700"
                subtitle="Current month"
              />
            </div>

            {/* Distribution Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* District Distribution */}
              <Card className="p-6 bg-white border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-blue-700" />
                  <h3 className="text-lg font-semibold text-gray-900">By District/Region</h3>
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {districtData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{item.district}</p>
                        <p className="text-xs text-gray-600">{item.user_count} users</p>
                      </div>
                      <Badge className="bg-blue-600 text-white">
                        {item.login_count} logins
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Caste Distribution */}
              <Card className="p-6 bg-white border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-purple-700" />
                  <h3 className="text-lg font-semibold text-gray-900">By Caste Category</h3>
                </div>
                <div className="space-y-3">
                  {casteData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{item.caste}</p>
                        <p className="text-xs text-gray-600">{item.user_count} users</p>
                      </div>
                      <Badge className="bg-purple-600 text-white">
                        {item.login_count} logins
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Income Distribution */}
              <Card className="p-6 bg-white border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="w-5 h-5 text-green-700" />
                  <h3 className="text-lg font-semibold text-gray-900">By Income Range</h3>
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {incomeData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{item.income_range}</p>
                        <p className="text-xs text-gray-600">{item.user_count} users</p>
                      </div>
                      <Badge className="bg-green-600 text-white">
                        {item.login_count} logins
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Recent Logins */}
            <Card className="p-6 bg-white border border-gray-200 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Login Activity</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">District</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Caste</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Income</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Login Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogins.map((login, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-800">{login.user_name}</td>
                        <td className="py-3 px-4 text-gray-700">{login.user_district}</td>
                        <td className="py-3 px-4">
                          <Badge className="bg-purple-600 text-white text-xs">{login.user_caste}</Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-700 text-sm">{login.user_income}</td>
                        <td className="py-3 px-4 text-gray-600 text-sm">
                          {new Date(login.login_time).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* All Users Table */}
            <Card className="p-6 bg-white border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">All Registered Users ({allUsers.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Phone</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">District</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Age</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Caste</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Income</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Total Logins</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Registered</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Terms</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Training Data</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((user, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-800 font-medium">{user.name}</td>
                        <td className="py-3 px-4 text-gray-700">{user.phone}</td>
                        <td className="py-3 px-4 text-gray-700">{user.district}</td>
                        <td className="py-3 px-4 text-gray-700">{user.age}</td>
                        <td className="py-3 px-4">
                          <Badge className="bg-purple-600 text-white text-xs">{user.caste}</Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-700 text-sm">{user.household_income}</td>
                        <td className="py-3 px-4">
                          <Badge className="bg-green-600 text-white">{user.total_logins}</Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-600 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          {user.terms_accepted ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              Accepted
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              <XCircle className="w-3 h-3 inline mr-1" />
                              Not Accepted
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {user.training_data_opted_in === false ? (
                            <Badge
                              className="bg-amber-100 text-amber-800 text-xs cursor-pointer"
                              onClick={() => openConsentHistory(user)}
                              data-testid={`opt-status-${user.id}`}
                            >
                              <XCircle className="w-3 h-3 inline mr-1" />
                              Opted out
                            </Badge>
                          ) : (
                            <Badge
                              className="bg-emerald-100 text-emerald-700 text-xs cursor-pointer"
                              onClick={() => openConsentHistory(user)}
                              data-testid={`opt-status-${user.id}`}
                            >
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              Opted in
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const token = localStorage.getItem('token');
                                window.open(`${API_URL}/api/auth/admin/users/${user.id}/download-agreement?token=${token}`, '_blank');
                              }}
                              className="text-xs border-green-600 text-green-700 hover:bg-green-50"
                              data-testid={`download-agreement-${user.id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Agreement
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(user.id, user.name)}
                              className="text-xs border-red-500 text-red-600 hover:bg-red-50"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            {/* KMS Smart Training Data */}
            <Card className="p-6 bg-white border border-gray-200 mt-6" data-testid="kms-training-data">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-indigo-600" />
                  KMS Smart — Training Data
                </h3>
                <div className="flex gap-2">
                  {['overview', 'entries', 'export'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => {
                        setTrainingTab(tab);
                        if (tab === 'entries' && trainingEntries.length === 0) loadTrainingEntries(0);
                      }}
                      data-testid={`training-tab-${tab}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        trainingTab === tab
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tab === 'overview' ? 'Overview' : tab === 'entries' ? 'All Entries' : 'Export'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab 1 — Overview */}
              {trainingTab === 'overview' && (
                <div>
                  {occupationStats ? (
                    occupationStats.total_analyses > 0 ? (
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <div className="bg-indigo-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-indigo-700">{occupationStats.total_analyses}</p>
                            <p className="text-xs text-indigo-600">Total Analyses</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-green-700">{occupationStats.subscriber_breakdown?.subscribers || 0}</p>
                            <p className="text-xs text-green-600">Subscribers</p>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-orange-700">{occupationStats.subscriber_breakdown?.free_users || 0}</p>
                            <p className="text-xs text-orange-600">Free Users</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-purple-700">{occupationStats.breakdown?.filter(b => b.count > 0).length || 0}</p>
                            <p className="text-xs text-purple-600">Occupation Types</p>
                          </div>
                          <div className="bg-emerald-50 rounded-lg p-3 text-center" data-testid="stat-opted-in">
                            <p className="text-2xl font-bold text-emerald-700">{occupationStats.consent_breakdown?.opted_in || 0}</p>
                            <p className="text-xs text-emerald-600">Opted In</p>
                          </div>
                          <div className="bg-amber-50 rounded-lg p-3 text-center" data-testid="stat-opted-out">
                            <p className="text-2xl font-bold text-amber-700">{occupationStats.consent_breakdown?.opted_out || 0}</p>
                            <p className="text-xs text-amber-600">Opted Out</p>
                          </div>
                        </div>

                        {/* Occupation Bar Chart */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Occupation Breakdown</h4>
                          <div className="space-y-1.5" data-testid="occupation-bar-chart">
                            {occupationStats.breakdown
                              .filter(item => item.count > 0)
                              .sort((a, b) => b.count - a.count)
                              .map((item) => {
                                const maxCount = Math.max(...occupationStats.breakdown.map(b => b.count), 1);
                                const barWidth = Math.max((item.count / maxCount) * 100, 2);
                                return (
                                  <div key={item.category} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-700 w-40 truncate text-right" title={item.category}>{item.category}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                                      <div
                                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                        style={{ width: `${barWidth}%`, minWidth: '28px' }}
                                      >
                                        <span className="text-[10px] font-bold text-white">{item.count}</span>
                                      </div>
                                    </div>
                                    <span className="text-xs text-gray-500 w-12 text-right">{item.percentage}%</span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>

                        {/* Platform & Language side-by-side */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Platform Breakdown</h4>
                            <div className="space-y-1">
                              {(occupationStats.platform_breakdown || []).map((p) => (
                                <div key={p.platform} className="flex justify-between items-center bg-gray-50 rounded px-3 py-1.5">
                                  <span className="text-sm text-gray-700 capitalize">{p.platform || 'web'}</span>
                                  <span className="text-sm font-medium text-gray-900">{p.count}</span>
                                </div>
                              ))}
                              {(!occupationStats.platform_breakdown || occupationStats.platform_breakdown.length === 0) && (
                                <p className="text-xs text-gray-400">No platform data yet</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Language Breakdown</h4>
                            <div className="space-y-1">
                              {(occupationStats.language_breakdown || []).map((l) => (
                                <div key={l.code} className="flex justify-between items-center bg-gray-50 rounded px-3 py-1.5">
                                  <span className="text-sm text-gray-700">{l.language}</span>
                                  <span className="text-sm font-medium text-gray-900">{l.count}</span>
                                </div>
                              ))}
                              {(!occupationStats.language_breakdown || occupationStats.language_breakdown.length === 0) && (
                                <p className="text-xs text-gray-400">No language data yet</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Occupation Table */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">All Occupation Categories</h4>
                          <div className="overflow-x-auto" data-testid="occupation-table">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-3 font-semibold text-gray-900">Occupation Category</th>
                                  <th className="text-right py-2 px-3 font-semibold text-gray-900">Count</th>
                                  <th className="text-right py-2 px-3 font-semibold text-gray-900">Percentage</th>
                                </tr>
                              </thead>
                              <tbody>
                                {occupationStats.breakdown.map((item) => (
                                  <tr key={item.category} className="border-b border-gray-50 hover:bg-gray-50">
                                    <td className="py-2 px-3 text-gray-800">{item.category}</td>
                                    <td className="py-2 px-3 text-right font-medium text-gray-900">{item.count}</td>
                                    <td className="py-2 px-3 text-right text-gray-600">{item.percentage}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-8 text-center">No KMS Smart analyses yet. Data will appear here once users start using KMS Smart.</p>
                    )
                  ) : (
                    <p className="text-sm text-gray-400 py-8 text-center">Loading stats...</p>
                  )}
                </div>
              )}

              {/* Tab 2 — All Entries */}
              {trainingTab === 'entries' && (
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="training-filters">
                    <input
                      type="date"
                      value={trainingFilters.date_from}
                      onChange={(e) => handleTrainingFilterChange('date_from', e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                      placeholder="From date"
                      data-testid="filter-date-from"
                    />
                    <input
                      type="date"
                      value={trainingFilters.date_to}
                      onChange={(e) => handleTrainingFilterChange('date_to', e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                      placeholder="To date"
                      data-testid="filter-date-to"
                    />
                    <select
                      value={trainingFilters.occupation}
                      onChange={(e) => handleTrainingFilterChange('occupation', e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                      data-testid="filter-occupation"
                    >
                      <option value="">All Occupations</option>
                      {['Agriculture/Farming','IT/Technology','Banking/Finance','Business/Self-employed','Accountant','Homemaker','Government Service','Healthcare','Handicrafts/Artisan','Daily Wage Worker','Arts/Media','Student','Sales/Retail','Teacher/Professor','Unknown/Not selected'].map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                    <select
                      value={trainingFilters.platform}
                      onChange={(e) => handleTrainingFilterChange('platform', e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                      data-testid="filter-platform"
                    >
                      <option value="">All Platforms</option>
                      <option value="web">Web</option>
                      <option value="android">Android</option>
                      <option value="ios">iOS</option>
                    </select>
                    <select
                      value={trainingFilters.language}
                      onChange={(e) => handleTrainingFilterChange('language', e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                      data-testid="filter-language"
                    >
                      <option value="">All Languages</option>
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                      <option value="te">Telugu</option>
                      <option value="ta">Tamil</option>
                    </select>
                    <input
                      type="text"
                      value={trainingFilters.location}
                      onChange={(e) => handleTrainingFilterChange('location', e.target.value)}
                      placeholder="Search location..."
                      className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                      data-testid="filter-location"
                    />
                  </div>

                  <p className="text-xs text-gray-500">Showing {trainingEntries.length} of {trainingTotal} entries</p>

                  {/* Table */}
                  <div className="overflow-x-auto" data-testid="training-entries-table">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left py-2 px-2 font-semibold text-gray-700">Date</th>
                          <th className="text-left py-2 px-2 font-semibold text-gray-700 min-w-[200px]">Profile Text</th>
                          <th className="text-left py-2 px-2 font-semibold text-gray-700">Occupation</th>
                          <th className="text-left py-2 px-2 font-semibold text-gray-700">Location</th>
                          <th className="text-left py-2 px-2 font-semibold text-gray-700">Policy</th>
                          <th className="text-center py-2 px-2 font-semibold text-gray-700">Lang</th>
                          <th className="text-left py-2 px-2 font-semibold text-gray-700">Benefit</th>
                          <th className="text-center py-2 px-2 font-semibold text-gray-700">Schemes</th>
                          <th className="text-center py-2 px-2 font-semibold text-gray-700">Platform</th>
                          <th className="text-center py-2 px-2 font-semibold text-gray-700">Type</th>
                          <th className="text-center py-2 px-2 font-semibold text-gray-700">Consent</th>
                          <th className="text-center py-2 px-2 font-semibold text-gray-700">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Bundle every follow-up *into* its parent analysis row so each
                          // analysis is exactly ONE row in the table. Q&A becomes part of
                          // the expanded panel, never a separate row.
                          const parents = trainingEntries.filter(e => e.record_type !== 'followup');
                          const parentIds = new Set(parents.map(p => p.id).filter(Boolean));
                          const followupsByParent = {};
                          const orphanFollowups = [];
                          trainingEntries.forEach((e) => {
                            if (e.record_type !== 'followup') return;
                            if (e.parent_analysis_id && parentIds.has(e.parent_analysis_id)) {
                              if (!followupsByParent[e.parent_analysis_id]) followupsByParent[e.parent_analysis_id] = [];
                              followupsByParent[e.parent_analysis_id].push(e);
                            } else {
                              orphanFollowups.push(e);
                            }
                          });
                          // Sort each parent's Q&A list chronologically (first question first)
                          Object.values(followupsByParent).forEach(kids =>
                            kids.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                          );
                          // Build flat row list: 1 row per parent (with its Q&A bundled),
                          // then any orphan follow-ups (parent not on this page) at the bottom.
                          const ordered = [];
                          trainingEntries.forEach((e) => {
                            if (e.record_type === 'followup') return;
                            ordered.push({
                              entry: e,
                              followups: followupsByParent[e.id] || [],
                              isChild: false,
                              parentTimeLabel: null,
                            });
                          });
                          orphanFollowups.forEach((e) => {
                            ordered.push({
                              entry: e,
                              followups: [],
                              isChild: false,
                              parentTimeLabel: 'Parent analysis not on this page',
                            });
                          });

                          return ordered.map(({ entry, followups, isChild, parentTimeLabel }, idx) => (
                          <React.Fragment key={entry.id || idx}>
                            <tr className={`border-b border-gray-50 hover:bg-gray-50 ${isChild ? 'bg-purple-50/40' : ''}`}>
                              <td className="py-2 px-2 text-gray-600 whitespace-nowrap">
                                {isChild && (
                                  <span className="inline-block mr-1 text-purple-400" aria-hidden>↳</span>
                                )}
                                {new Date(entry.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                                <br />
                                <span className="text-[10px] text-gray-400">{new Date(entry.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                              </td>
                              <td className="py-2 px-2 text-gray-800 max-w-[260px] align-top">
                                <div className={`flex items-start gap-1.5 ${isChild ? 'pl-4' : ''}`}>
                                  {entry.record_type === 'followup' && (
                                    <span
                                      className="shrink-0 mt-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700"
                                      data-testid={`record-type-badge-${idx}`}
                                      title="Follow-up question recorded after the initial analysis"
                                    >
                                      Q&amp;A
                                    </span>
                                  )}
                                  {followups.length > 0 && (
                                    <span
                                      className="shrink-0 mt-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700"
                                      data-testid={`followup-count-${idx}`}
                                      title={`${followups.length} follow-up question${followups.length > 1 ? 's' : ''} bundled into this row — expand Details to read`}
                                    >
                                      💬 {followups.length}
                                    </span>
                                  )}
                                  <span className="block truncate flex-1 min-w-0" title={entry.profile_text}>{entry.profile_text}</span>
                                </div>
                                {parentTimeLabel && (
                                  <p className="text-[9px] text-amber-700 mt-0.5 italic">{parentTimeLabel}</p>
                                )}
                              </td>
                              <td className="py-2 px-2 text-gray-700">
                                <span className="inline-block bg-indigo-50 text-indigo-700 text-[10px] px-1.5 py-0.5 rounded">{entry.occupation?.category || 'N/A'}</span>
                              </td>
                              <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{entry.location || 'N/A'}</td>
                              <td className="py-2 px-2 text-gray-700">
                                {Array.isArray(entry.policy_event) ? entry.policy_event.join(', ') : entry.policy_event || 'N/A'}
                              </td>
                              <td className="py-2 px-2 text-center">
                                <span className="uppercase text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{entry.language || 'en'}</span>
                              </td>
                              <td className="py-2 px-2 text-gray-700 whitespace-nowrap text-[10px]">{entry.total_benefit || 'N/A'}</td>
                              <td className="py-2 px-2 text-center font-medium">{entry.schemes_matched || 0}</td>
                              <td className="py-2 px-2 text-center">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${
                                  entry.platform === 'ios' ? 'bg-blue-50 text-blue-700' :
                                  entry.platform === 'android' ? 'bg-green-50 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>{entry.platform || 'web'}</span>
                              </td>
                              <td className="py-2 px-2 text-center">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                  entry.is_subscriber ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-600'
                                }`}>{entry.is_subscriber ? 'Premium' : 'Free'}</span>
                              </td>
                              <td className="py-2 px-2 text-center" data-testid={`consent-cell-${idx}`}>
                                {entry.is_opted_in === false ? (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
                                    Opted out
                                  </span>
                                ) : (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                                    Opted in
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-2 text-center">
                                <button
                                  onClick={() => setExpandedEntry(expandedEntry === idx ? null : idx)}
                                  className="text-indigo-600 hover:text-indigo-800 text-[10px] underline"
                                  data-testid={`expand-entry-${idx}`}
                                >
                                  {expandedEntry === idx ? 'Collapse' : 'Expand'}
                                </button>
                              </td>
                            </tr>
                            {expandedEntry === idx && (
                              <tr>
                                <td colSpan={11} className="bg-indigo-50 p-4">
                                  <div className="space-y-3 text-xs">
                                    {entry.record_type === 'followup' && (
                                      <div className="bg-purple-50 border border-purple-200 rounded p-3" data-testid={`followup-block-${idx}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-200 text-purple-800">Follow-up Q&amp;A</span>
                                          <span className="text-[10px] text-purple-700">recorded after KMS Smart analysis</span>
                                        </div>
                                        <div className="mb-2">
                                          <h5 className="font-semibold text-gray-800 mb-1">Question</h5>
                                          <p className="text-gray-800 bg-white rounded p-2">{entry.question || entry.full_ai_response?.question || entry.profile_text}</p>
                                        </div>
                                        <div>
                                          <h5 className="font-semibold text-gray-800 mb-1">AI Answer</h5>
                                          <p className="text-gray-700 bg-white rounded p-2 whitespace-pre-wrap">{entry.full_ai_response?.answer || entry.full_ai_response?.insight || 'N/A'}</p>
                                        </div>
                                        {Array.isArray(entry.full_ai_response?.context_schemes) && entry.full_ai_response.context_schemes.length > 0 && (
                                          <div className="mt-2">
                                            <h5 className="font-semibold text-gray-800 mb-1">Context schemes from prior analysis</h5>
                                            <p className="text-gray-700 text-[11px]">{entry.full_ai_response.context_schemes.join(', ')}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <div>
                                      <h5 className="font-semibold text-gray-800 mb-1">Full Profile Text</h5>
                                      <p className="text-gray-700 bg-white rounded p-2">{entry.profile_text}</p>
                                    </div>
                                    {entry.full_ai_response?.insight && (
                                      <div>
                                        <h5 className="font-semibold text-gray-800 mb-1">AI Insight</h5>
                                        <p className="text-gray-700 whitespace-pre-wrap">{entry.full_ai_response.insight}</p>
                                      </div>
                                    )}
                                    {entry.full_ai_response?.schemes?.length > 0 && (
                                      <div>
                                        <h5 className="font-semibold text-gray-800 mb-1">Schemes Matched ({entry.full_ai_response.schemes.length})</h5>
                                        <ul className="list-disc list-inside text-gray-700 space-y-0.5">
                                          {entry.full_ai_response.schemes.map((s, si) => (
                                            <li key={si}><strong>{s.name || s.scheme_name}</strong> — {s.potential_benefit || s.benefit || ''}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {entry.full_ai_response?.action_steps?.length > 0 && (
                                      <div>
                                        <h5 className="font-semibold text-gray-800 mb-1">Action Steps</h5>
                                        <ol className="list-decimal list-inside text-gray-700 space-y-0.5">
                                          {entry.full_ai_response.action_steps.map((step, si) => (
                                            <li key={si}>{typeof step === 'string' ? step : step.step || step.description || JSON.stringify(step)}</li>
                                          ))}
                                        </ol>
                                      </div>
                                    )}
                                    {entry.occupation?.additional_details && (
                                      <div>
                                        <h5 className="font-semibold text-gray-800 mb-1">Occupation Details</h5>
                                        <div className="flex flex-wrap gap-2">
                                          {Object.entries(entry.occupation.additional_details).filter(([,v]) => v).map(([k, v]) => (
                                            <span key={k} className="bg-white px-2 py-0.5 rounded text-gray-700">{k.replace(/_/g, ' ')}: {v}</span>
                                          ))}
                                          {Object.entries(entry.occupation.additional_details).filter(([,v]) => v).length === 0 && (
                                            <span className="text-gray-400">No additional details</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {entry.llm_parsed_profile && (
                                      <div>
                                        <h5 className="font-semibold text-gray-800 mb-1">Parsed Profile</h5>
                                        <pre className="bg-white rounded p-2 text-[10px] text-gray-600 overflow-x-auto">{JSON.stringify(entry.llm_parsed_profile, null, 2)}</pre>
                                      </div>
                                    )}

                                    {/* Bundled follow-up Q&A — every conversation under this analysis in one place */}
                                    {followups && followups.length > 0 && (
                                      <div className="border-t-2 border-purple-200 pt-3 mt-3" data-testid={`followups-bundle-${idx}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-purple-200 text-purple-800">💬 Follow-up Q&amp;A ({followups.length})</span>
                                          <span className="text-[10px] text-purple-600">conversation history with this user, oldest first</span>
                                        </div>
                                        <div className="space-y-2">
                                          {followups.map((fu, fi) => (
                                            <div key={fu.id || fi} className="bg-purple-50 border border-purple-200 rounded p-3" data-testid={`followup-item-${idx}-${fi}`}>
                                              <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-[10px] font-semibold text-purple-800">Q{fi + 1}</span>
                                                <span className="text-[10px] text-gray-500">{new Date(fu.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                              </div>
                                              <p className="text-gray-900 bg-white rounded p-2 mb-2 text-[11px]">
                                                <strong className="text-purple-700">Q:</strong> {fu.question || fu.full_ai_response?.question || (fu.profile_text || '').replace(/^\[Follow-up Q\]\s*/, '')}
                                              </p>
                                              <p className="text-gray-700 bg-white rounded p-2 whitespace-pre-wrap text-[11px]">
                                                <strong className="text-purple-700">A:</strong> {fu.full_ai_response?.answer || fu.full_ai_response?.insight || 'N/A'}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          ));
                        })()}
                        {trainingEntries.length === 0 && (
                          <tr>
                            <td colSpan={11} className="py-8 text-center text-sm text-gray-500">No training data entries found. Adjust filters or wait for users to use KMS Smart.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex justify-between items-center pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={trainingPage === 0}
                      onClick={() => loadTrainingEntries(trainingPage - 1)}
                      className="text-xs"
                      data-testid="training-prev-page"
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-gray-500">Page {trainingPage + 1} of {Math.max(1, Math.ceil(trainingTotal / 25))}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!trainingHasMore}
                      onClick={() => loadTrainingEntries(trainingPage + 1)}
                      className="text-xs"
                      data-testid="training-next-page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {/* Tab 3 — Export */}
              {trainingTab === 'export' && (
                <div className="space-y-6 py-4" data-testid="training-export">
                  <p className="text-sm text-gray-600">Export KMS Smart training data for analysis. Optionally filter by date range before exporting.</p>
                  
                  <div className="flex gap-3 items-end">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">From Date</label>
                      <input
                        type="date"
                        value={exportDateFrom}
                        onChange={(e) => setExportDateFrom(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        data-testid="export-date-from"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">To Date</label>
                      <input
                        type="date"
                        value={exportDateTo}
                        onChange={(e) => setExportDateTo(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        data-testid="export-date-to"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleExportTrainingData('csv')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="export-csv-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download CSV
                    </Button>
                    <Button
                      onClick={() => handleExportTrainingData('json')}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="export-json-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download JSON
                    </Button>
                  </div>

                  <p className="text-xs text-gray-400">
                    Total records available: {occupationStats?.total_analyses || 0}. 
                    {exportDateFrom || exportDateTo ? ' Filtered by selected date range.' : ' All records will be exported.'}
                  </p>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Add Scheme Tab */}
        {activeTab === 'add-scheme' && (
          <div>
            <Card className="p-8 bg-white border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Add New Scheme Manually</h2>
              <p className="text-gray-600 mb-6">Enter scheme details below. All fields are required.</p>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                setLoading(true);
                setError('');
                setSuccess('');

                const formData = new FormData(e.target);
                const schemeData = {
                  name: formData.get('name'),
                  state: formData.get('state'),
                  type: formData.get('type'),
                  sector: formData.get('sector'),
                  caste: formData.get('caste').split(',').map(c => c.trim()),
                  shortDescription: formData.get('shortDescription'),
                  description: formData.get('description'),
                  eligibility: formData.get('eligibility').split('\n').filter(e => e.trim()),
                  benefits: formData.get('benefits').split('\n').filter(b => b.trim()),
                  documents: formData.get('documents').split('\n').filter(d => d.trim()),
                  howToApply: formData.get('howToApply'),
                  deadline: formData.get('deadline'),
                  applicationUrl: formData.get('applicationUrl')
                };

                try {
                  await axios.post(`${API_URL}/api/schemes`, schemeData);
                  setSuccess('Scheme added successfully! It will appear immediately on the schemes page.');
                  e.target.reset();
                  
                  // Scroll to top to show success message
                  window.scrollTo(0, 0);
                } catch (error) {
                  setError(`Failed to add scheme: ${error.response?.data?.detail || error.message}`);
                  window.scrollTo(0, 0);
                } finally {
                  setLoading(false);
                }
              }} className="space-y-6">
                
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Scheme Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g., PM-KISAN Scheme"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">State *</label>
                    <select
                      name="state"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Select State</option>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Telangana">Telangana</option>
                      <option value="Karnataka">Karnataka</option>
                      <option value="Central">Central (All India)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Type *</label>
                    <select
                      name="type"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Select Type</option>
                      <option value="state">State Level</option>
                      <option value="central">Central Level</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Sector *</label>
                    <select
                      name="sector"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Select Sector</option>
                      <option value="Agriculture">Agriculture</option>
                      <option value="Education">Education</option>
                      <option value="Health">Health</option>
                      <option value="Women & Child">Women & Child</option>
                      <option value="Social Welfare">Social Welfare</option>
                      <option value="Business & Entrepreneurship">Business & Entrepreneurship</option>
                      <option value="Housing">Housing</option>
                      <option value="Skills & Employment">Skills & Employment</option>
                      <option value="Senior Citizen">Senior Citizen</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Applicable Castes * (comma-separated)</label>
                    <input
                      type="text"
                      name="caste"
                      required
                      placeholder="e.g., All or SC, ST, OBC"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter "All" for all castes, or specific categories separated by commas</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Application Deadline *</label>
                    <input
                      type="text"
                      name="deadline"
                      required
                      placeholder="e.g., Open throughout the year"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Description Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Short Description * (max 100 chars)</label>
                  <input
                    type="text"
                    name="shortDescription"
                    required
                    maxLength={100}
                    placeholder="Brief one-line description"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Full Description *</label>
                  <textarea
                    name="description"
                    required
                    rows={4}
                    placeholder="Detailed description of the scheme (2-3 sentences)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                {/* List Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Eligibility Criteria * (one per line)</label>
                  <textarea
                    name="eligibility"
                    required
                    rows={4}
                    placeholder="Must be a farmer&#10;Age between 18-60&#10;Annual income below ₹5 lakh"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Benefits * (one per line)</label>
                  <textarea
                    name="benefits"
                    required
                    rows={4}
                    placeholder="₹6,000 per year in three installments&#10;Direct Benefit Transfer to bank account&#10;No middlemen involved"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Required Documents * (one per line)</label>
                  <textarea
                    name="documents"
                    required
                    rows={4}
                    placeholder="Aadhaar Card&#10;Bank account details&#10;Land ownership documents&#10;Ration card"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">How to Apply *</label>
                  <textarea
                    name="howToApply"
                    required
                    rows={3}
                    placeholder="Visit nearest office or apply online through official portal"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Application URL *</label>
                  <input
                    type="url"
                    name="applicationUrl"
                    required
                    placeholder="https://example.gov.in/scheme"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex gap-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-3 text-lg disabled:opacity-50"
                  >
                    {loading ? 'Adding Scheme...' : 'Add Scheme'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear the form?')) {
                        document.querySelector('form').reset();
                      }
                    }}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Clear Form
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {/* Help Requests Tab */}
        {/* All Schemes Tab */}
        {activeTab === 'all-schemes' && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">All Schemes Database</h2>
                <p className="text-gray-600">Complete access to all {allSchemes.length} schemes (Admin only)</p>
              </div>
              <Button
                onClick={loadAllSchemes}
                variant="outline"
                className="text-gray-600"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* Recently Added Schemes Section */}
            {(() => {
              const sevenDaysAgo = new Date();
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
              const recentlyAdded = allSchemes
                .filter(s => s.created_at && new Date(s.created_at) > sevenDaysAgo)
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              
              if (recentlyAdded.length > 0) {
                return (
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-green-800">Recently Added via Scraping</h3>
                        <Badge className="bg-green-600 text-white">{recentlyAdded.length} new</Badge>
                      </div>
                      <p className="text-sm text-green-700 mb-4">These schemes were added in the last 7 days. Review them to ensure accuracy.</p>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {recentlyAdded.slice(0, 10).map((scheme, idx) => (
                          <div 
                            key={scheme.id} 
                            className="bg-white rounded-lg p-3 border border-green-100 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => {
                              setExpandedScheme(scheme.id);
                              // Scroll to scheme in main list
                              document.getElementById(`scheme-${scheme.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-sm">{idx + 1}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <Badge className="bg-green-100 text-green-800 text-xs">NEW</Badge>
                                  <Badge className={`text-xs ${
                                    scheme.state === 'Central' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                  }`}>
                                    {scheme.state}
                                  </Badge>
                                  <span className="text-xs text-gray-500">
                                    Added: {new Date(scheme.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-sm">{scheme.name}</h4>
                                <p className="text-gray-600 text-xs line-clamp-1">{scheme.shortDescription || scheme.description?.substring(0, 100)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {recentlyAdded.length > 10 && (
                          <p className="text-center text-sm text-green-600 pt-2">
                            +{recentlyAdded.length - 10} more recently added schemes (scroll down to see all)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search schemes by name or sector..."
                  value={schemeSearch}
                  onChange={(e) => setSchemeSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Schemes Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{allSchemes.length}</p>
                    <p className="text-sm text-gray-600">Total Schemes</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-green-50 border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-700">
                      {allSchemes.filter(s => {
                        const sevenDaysAgo = new Date();
                        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                        return s.created_at && new Date(s.created_at) > sevenDaysAgo;
                      }).length}
                    </p>
                    <p className="text-sm text-green-600">Added (7 days)</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Unlock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {allSchemes.filter(s => !s.is_premium && !s.is_locked).length}
                    </p>
                    <p className="text-sm text-gray-600">Free Schemes</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Crown className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">
                      {allSchemes.filter(s => s.is_premium || s.is_locked).length}
                    </p>
                    <p className="text-sm text-gray-600">Premium Schemes</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="p-6 bg-white max-w-md mx-4 shadow-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Delete Scheme?</h3>
                      <p className="text-sm text-gray-600">This action cannot be undone</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to delete "<strong>{deleteConfirm.name}</strong>"?
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setDeleteConfirm(null)}
                      className="flex-1"
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => deleteScheme(deleteConfirm.id, deleteConfirm.name)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      disabled={deleting}
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {/* Schemes List */}
            <div className="space-y-4">
              {allSchemes
                .filter(scheme => 
                  schemeSearch === '' || 
                  scheme.name.toLowerCase().includes(schemeSearch.toLowerCase()) ||
                  scheme.sector?.toLowerCase().includes(schemeSearch.toLowerCase()) ||
                  scheme.state?.toLowerCase().includes(schemeSearch.toLowerCase())
                )
                .sort((a, b) => {
                  // Sort by created_at descending (newest first)
                  if (a.created_at && b.created_at) {
                    return new Date(b.created_at) - new Date(a.created_at);
                  }
                  if (a.created_at) return -1;
                  if (b.created_at) return 1;
                  return 0;
                })
                .map((scheme, index) => {
                  const sevenDaysAgo = new Date();
                  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                  const isRecentlyAdded = scheme.created_at && new Date(scheme.created_at) > sevenDaysAgo;
                  
                  return (
                <Card 
                  key={scheme.id || index}
                  id={`scheme-${scheme.id}`}
                  className={`bg-white border overflow-hidden ${isRecentlyAdded ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-200'}`}
                >
                  {/* Scheme Header - Always Visible */}
                  <div 
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${isRecentlyAdded ? 'bg-green-50/50' : ''}`}
                    onClick={() => setExpandedScheme(expandedScheme === scheme.id ? null : scheme.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Serial Number - Admin Only */}
                      <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center shadow-md ${isRecentlyAdded ? 'bg-gradient-to-br from-green-600 to-green-700' : 'bg-gradient-to-br from-gray-700 to-gray-900'}`}>
                        <span className="text-white font-bold text-lg">{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {isRecentlyAdded && (
                            <Badge className="bg-green-600 text-white text-xs animate-pulse">
                              <Sparkles className="w-3 h-3 inline mr-1" />
                              NEW
                            </Badge>
                          )}
                          <Badge className={`text-xs ${
                            scheme.state === 'Central' ? 'bg-blue-100 text-blue-800' :
                            scheme.state === 'Andhra Pradesh' ? 'bg-orange-100 text-orange-800' :
                            scheme.state === 'Telangana' ? 'bg-pink-100 text-pink-800' :
                            scheme.state === 'Karnataka' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {scheme.state}
                          </Badge>
                          <Badge className={`text-xs ${
                            scheme.type?.toLowerCase() === 'central' ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {scheme.type}
                          </Badge>
                          <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                            {scheme.sector}
                          </Badge>
                          {scheme.is_premium || scheme.is_locked ? (
                            <Badge className="bg-purple-100 text-purple-800 text-xs">
                              <Crown className="w-3 h-3 inline mr-1" />
                              Premium
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              <Unlock className="w-3 h-3 inline mr-1" />
                              Free
                            </Badge>
                          )}
                          {scheme.created_at && (
                            <span className="text-xs text-gray-500 ml-auto">
                              Added: {new Date(scheme.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900 text-lg mb-1">{scheme.name}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2">{scheme.shortDescription || scheme.description?.substring(0, 150)}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {expandedScheme === scheme.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedScheme === scheme.id && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-4">
                          {/* Full Description */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-600" />
                              Description
                            </h4>
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">{scheme.description}</p>
                          </div>

                          {/* Eligibility */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Eligibility Criteria
                            </h4>
                            <ul className="text-sm text-gray-700 space-y-1">
                              {(scheme.eligibility || []).map((item, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-green-600 mt-1">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Benefits */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-yellow-600" />
                              Benefits
                            </h4>
                            <ul className="text-sm text-gray-700 space-y-1">
                              {(scheme.benefits || []).map((item, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-yellow-600 mt-1">★</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                          {/* Documents Required */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4 text-purple-600" />
                              Required Documents
                            </h4>
                            <ul className="text-sm text-gray-700 space-y-1">
                              {(scheme.documents || []).map((item, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-purple-600 mt-1">📄</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* How to Apply */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <ArrowRight className="w-4 h-4 text-blue-600" />
                              How to Apply
                            </h4>
                            <p className="text-sm text-gray-700">{scheme.howToApply}</p>
                          </div>

                          {/* Caste Eligibility */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Caste Eligibility</h4>
                            <div className="flex flex-wrap gap-2">
                              {(scheme.caste || []).map((c, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {c}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          {/* Deadline & Application URL */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1 text-sm">Deadline</h4>
                              <p className="text-sm text-gray-700">{scheme.deadline || 'Ongoing'}</p>
                            </div>
                            {scheme.applicationUrl && (
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-1 text-sm">Apply Link</h4>
                                <a 
                                  href={scheme.applicationUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline truncate block"
                                >
                                  Visit Portal →
                                </a>
                              </div>
                            )}
                          </div>

                          {/* Scheme ID */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Scheme ID</h4>
                            <p className="text-xs text-gray-500 font-mono">{scheme.id}</p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap gap-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/schemes/${scheme.slug || scheme.id}`)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Public Page
                        </Button>
                        
                        {/* Edit Tier Button */}
                        {scheme.is_premium || scheme.is_locked ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateSchemeTier(scheme.id, false, scheme.name)}
                            disabled={loading}
                            className="text-green-600 border-green-200 hover:bg-green-50"
                          >
                            <Unlock className="w-4 h-4 mr-2" />
                            Set as Free
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateSchemeTier(scheme.id, true, scheme.name)}
                            disabled={loading}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          >
                            <Crown className="w-4 h-4 mr-2" />
                            Set as Premium
                          </Button>
                        )}

                        {/* Delete Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm({ id: scheme.id, name: scheme.name })}
                          className="text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Scheme
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
                  );
                })}
            </div>

            {/* Empty State */}
            {allSchemes.filter(scheme => 
              schemeSearch === '' || 
              scheme.name.toLowerCase().includes(schemeSearch.toLowerCase()) ||
              scheme.sector?.toLowerCase().includes(schemeSearch.toLowerCase())
            ).length === 0 && (
              <Card className="p-8 bg-white border border-gray-200 text-center">
                <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No schemes found</h3>
                <p className="text-gray-500">Try a different search term</p>
              </Card>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Payments</h2>
                <p className="text-gray-600">Track all subscription payments and revenue</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                data-testid="delete-test-payments-btn"
                onClick={async () => {
                  if (!window.confirm('Delete ALL mock/test payments? This cannot be undone.')) return;
                  try {
                    const res = await axios.delete(`${API_URL}/api/payments/admin/delete-test-payments`);
                    alert(`Deleted ${res.data.deleted_subscriptions} test/mock payment(s).`);
                    loadPayments();
                  } catch (e) {
                    alert('Failed: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Test Payments
              </Button>
            </div>

            {/* Payment Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      ₹{paymentStats?.total_revenue?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{paymentStats?.total_payments || 0}</p>
                    <p className="text-sm text-gray-600">Total Payments</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Crown className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{paymentStats?.active_subscriptions || 0}</p>
                    <p className="text-sm text-gray-600">Active Subscriptions</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{paymentStats?.this_month || 0}</p>
                    <p className="text-sm text-gray-600">This Month</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Payments Table */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    data-testid="sync-razorpay-btn"
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API_URL}/api/payments/admin/sync-razorpay`);
                        const d = res.data;
                        alert(`Synced ${d.synced} new payment(s) from Razorpay.\n${d.already_recorded} already recorded.\n${d.failed_payments} failed/pending.`);
                        loadPayments();
                      } catch (e) {
                        alert('Sync failed: ' + (e.response?.data?.detail || e.message));
                      }
                    }}
                    className="text-green-700 border-green-300 hover:bg-green-50"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Sync from Razorpay
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={loadPayments}
                    className="text-gray-600"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>
              
              {payments.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No payments recorded yet</p>
                  <p className="text-sm text-gray-400 mt-1">Payments will appear here when users subscribe</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">User</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Phone</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Source</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Plan</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Amount</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Payment ID</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Expires</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment, index) => (
                        <tr key={payment.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-900">{payment.user_name || 'N/A'}</p>
                          </td>
                          <td className="py-3 px-4 text-gray-700">{payment.user_phone || 'N/A'}</td>
                          <td className="py-3 px-4">
                            <Badge className={`text-xs ${
                              payment.source === 'apple_iap' ? 'bg-gray-900 text-white' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {payment.source === 'apple_iap' ? 'Apple IAP' : 'Razorpay'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`text-xs ${
                              payment.plan_id === 'five_year' ? 'bg-purple-100 text-purple-800' : 
                              payment.plan_id === 'three_year' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {payment.plan_id === 'five_year' ? '5 Year Plan' : 
                               payment.plan_id === 'three_year' ? '3 Year Plan' : '1 Year Plan'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold text-green-600">₹{payment.amount?.toLocaleString()}</span>
                          </td>
                          <td className="py-3 px-4">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{payment.payment_id}</code>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`text-xs ${
                              payment.status === 'active' ? 'bg-green-100 text-green-800' :
                              payment.status === 'expired' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {payment.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {payment.end_date ? new Date(payment.end_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-3 px-4 flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`activate-sub-${payment.user_phone}`}
                              onClick={async () => {
                                try {
                                  const res = await axios.post(`${API_URL}/api/payments/admin/activate-subscription`, {
                                    phone: payment.user_phone,
                                    plan: payment.plan_id,
                                    expiry_date: payment.end_date
                                  });
                                  alert(`Subscription activated for ${res.data.user_name || payment.user_phone}!\nValid until: ${res.data.subscription_expiry?.slice(0,10)}`);
                                } catch (e) {
                                  alert('Failed: ' + (e.response?.data?.detail || e.message));
                                }
                              }}
                              className="text-xs text-green-700 border-green-300 hover:bg-green-50"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Activate
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`delete-payment-${payment.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Delete payment for ${payment.user_name}?`)) return;
                                try {
                                  await axios.delete(`${API_URL}/api/subscription/admin/payments/${payment.id}`);
                                  setPayments(prev => prev.filter(p => p.id !== payment.id));
                                  alert('Payment deleted');
                                } catch (e) {
                                  alert('Failed: ' + (e.response?.data?.detail || e.message));
                                }
                              }}
                              className="text-xs text-red-700 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* News Management Tab */}
        {activeTab === 'news' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">News Management</h2>
              <p className="text-gray-600">Manage and refresh news articles for all users</p>
            </div>

            {/* News Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Newspaper className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {newsMetadata?.news_count || 0}
                    </p>
                    <p className="text-sm text-gray-600">Total Articles</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {newsMetadata?.hours_ago !== undefined 
                        ? newsMetadata.hours_ago < 1 
                          ? 'Just now' 
                          : `${Math.round(newsMetadata.hours_ago)}h ago`
                        : 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">Last Updated</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">
                      {newsMetadata?.next_refresh_in !== undefined 
                        ? newsMetadata.next_refresh_in <= 0 
                          ? 'Now' 
                          : `${Math.round(newsMetadata.next_refresh_in)}h`
                        : 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">Next Auto-Refresh</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">6h</p>
                    <p className="text-sm text-gray-600">Refresh Interval</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Refresh Section */}
            <Card className="p-6 bg-white border border-gray-200 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Manual Refresh</h3>
                  <p className="text-sm text-gray-600">
                    Fetch latest news from all sources. This will update news for all users.
                  </p>
                  {newsMetadata?.last_updated && (
                    <p className="text-xs text-gray-500 mt-2">
                      Last updated: {new Date(newsMetadata.last_updated).toLocaleString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  )}
                </div>
                
                <Button
                  onClick={handleRefreshNews}
                  disabled={newsRefreshing}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
                  data-testid="admin-refresh-news-btn"
                >
                  {newsRefreshing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh News Now
                    </>
                  )}
                </Button>
              </div>
              
              {/* Refresh Message */}
              {newsMessage && (
                <div 
                  className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                    newsMessage.type === 'success' 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : newsMessage.type === 'error'
                      ? 'bg-red-50 text-red-800 border border-red-200'
                      : 'bg-blue-50 text-blue-800 border border-blue-200'
                  }`}
                  data-testid="news-refresh-message"
                >
                  {newsMessage.type === 'success' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : newsMessage.type === 'error' ? (
                    <XCircle className="w-5 h-5" />
                  ) : (
                    <Clock className="w-5 h-5" />
                  )}
                  <span>{newsMessage.text}</span>
                </div>
              )}
            </Card>

            {/* Tech News Refresh Section */}
            <Card className="p-6 bg-white border border-gray-200 mb-6" data-testid="admin-tech-news-card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-indigo-600" />
                    KMS TLDR Tech News
                  </h3>
                  <p className="text-sm text-gray-600">
                    Pull the latest global + India tech stories and AI-summarise them. Runs in the
                    background — new articles appear in 2-3 minutes. Use this to repopulate production.
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    <p className="text-xs text-gray-500" data-testid="tech-news-count">
                      Live articles: <span className="font-semibold text-gray-700">{techNewsCount ?? '—'}</span>
                    </p>
                    {techNewsLastUpdated && (
                      <p className="text-xs text-gray-500">
                        Last updated: {new Date(techNewsLastUpdated).toLocaleString('en-IN', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
                        })}
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handleRefreshTechNews}
                  disabled={techRefreshing}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2"
                  data-testid="admin-refresh-tech-news-btn"
                >
                  {techRefreshing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh Tech News
                    </>
                  )}
                </Button>
              </div>

              {techMessage && (
                <div
                  className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                    techMessage.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : techMessage.type === 'error'
                      ? 'bg-red-50 text-red-800 border border-red-200'
                      : 'bg-blue-50 text-blue-800 border border-blue-200'
                  }`}
                  data-testid="tech-news-refresh-message"
                >
                  {techMessage.type === 'success' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : techMessage.type === 'error' ? (
                    <XCircle className="w-5 h-5" />
                  ) : (
                    <Clock className="w-5 h-5" />
                  )}
                  <span>{techMessage.text}</span>
                </div>
              )}
            </Card>

            {/* Info Card */}
            <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                How News Refresh Works
              </h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  News is automatically refreshed every <strong>6 hours</strong> from multiple RSS feeds
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  Manual refresh fetches the latest articles immediately and updates for all users
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  AI summarization is applied to each article (may take 1-3 minutes for full refresh)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  Users see the updated news automatically on their News page
                </li>
              </ul>
            </Card>
          </div>
        )}

        {/* Mobile App Tab */}
        {activeTab === 'mobile' && (
          <div className="space-y-6">
            {/* Mobile Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Download className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">
                      {mobileStats?.total_devices || 0}
                    </p>
                    <p className="text-sm text-gray-600">Total Installs</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {mobileStats?.active_users?.last_24h || 0}
                    </p>
                    <p className="text-sm text-gray-600">Active (24h)</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {mobileStats?.notifications?.total_sent || 0}
                    </p>
                    <p className="text-sm text-gray-600">Notifications Sent</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">
                      {mobileStats?.new_installs?.this_week || 0}
                    </p>
                    <p className="text-sm text-gray-600">New This Week</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Device Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6 bg-white border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-600" />
                  Device Breakdown
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-medium text-gray-900">Android</span>
                    </div>
                    <span className="text-2xl font-bold text-green-600">
                      {mobileStats?.device_breakdown?.android || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-medium text-gray-900">iOS</span>
                    </div>
                    <span className="text-2xl font-bold text-gray-800">
                      {mobileStats?.device_breakdown?.ios || 0}
                    </span>
                  </div>
                </div>
                
                {/* Install Trends */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-gray-700 mb-3">Install Trends</h4>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <p className="text-lg font-bold text-purple-600">{mobileStats?.new_installs?.today || 0}</p>
                      <p className="text-xs text-gray-600">Today</p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <p className="text-lg font-bold text-purple-600">{mobileStats?.new_installs?.this_week || 0}</p>
                      <p className="text-xs text-gray-600">This Week</p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <p className="text-lg font-bold text-purple-600">{mobileStats?.new_installs?.this_month || 0}</p>
                      <p className="text-xs text-gray-600">This Month</p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Push Notification Composer */}
              <Card className="p-6 bg-white border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  Send Push Notification
                </h3>
                
                {error && (
                  <div className="mb-4 p-3 bg-red-50 text-red-800 rounded-lg flex items-start gap-2">
                    <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div className="whitespace-pre-line text-sm">{error}</div>
                  </div>
                )}
                
                {success && (
                  <div className="mb-4 p-3 bg-green-50 text-green-800 rounded-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {success}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notification Title *
                    </label>
                    <Input
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                      placeholder="e.g., New Scheme Alert!"
                      className="w-full"
                      maxLength={50}
                    />
                    <p className="text-xs text-gray-500 mt-1">{notificationTitle.length}/50 characters</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      value={notificationBody}
                      onChange={(e) => setNotificationBody(e.target.value)}
                      placeholder="e.g., 5 new government schemes have been added. Check them out now!"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      rows={3}
                      maxLength={150}
                    />
                    <p className="text-xs text-gray-500 mt-1">{notificationBody.length}/150 characters</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Platform
                    </label>
                    <div className="flex gap-3" data-testid="platform-selector">
                      {[
                        { value: 'all', label: 'All Devices', count: mobileStats?.total_devices || 0 },
                        { value: 'android', label: 'Android', count: mobileStats?.device_breakdown?.android || 0 },
                        { value: 'ios', label: 'iOS', count: mobileStats?.device_breakdown?.ios || 0 },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setNotificationPlatform(opt.value)}
                          data-testid={`platform-${opt.value}`}
                          className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                            notificationPlatform === opt.value
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                          }`}
                        >
                          {opt.label}
                          <span className="block text-xs mt-0.5 opacity-80">{opt.count} devices</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleSendNotification}
                    disabled={sendingNotification || !notificationTitle.trim() || !notificationBody.trim()}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-3"
                    data-testid="send-notification-btn"
                  >
                    {sendingNotification ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {notificationPlatform === 'all'
                          ? 'Send to All Mobile Users'
                          : `Send to ${notificationPlatform === 'ios' ? 'iOS' : 'Android'} Users`}
                      </>
                    )}
                  </Button>
                </div>
                
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-800">
                    <strong>Note:</strong> This will send a push notification to {
                      notificationPlatform === 'all'
                        ? `all registered mobile app users (${mobileStats?.total_devices || 0} devices)`
                        : `${notificationPlatform === 'ios' ? 'iOS' : 'Android'} users (${
                            notificationPlatform === 'ios'
                              ? mobileStats?.device_breakdown?.ios || 0
                              : mobileStats?.device_breakdown?.android || 0
                          } devices)`
                    }.
                  </p>
                </div>
              </Card>
            </div>

            {/* Personalized Notifications (auto per-user) */}
            <Card className="p-6 bg-white border border-gray-200" data-testid="admin-personalized-notifications-card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-indigo-600" />
                    Personalized Notifications
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                    <strong>Automatic (no action needed):</strong> up to <strong>5 notifications/user/day</strong> —
                    4 content items every 6 hours (a new scheme matching their state &amp; category, general news,
                    an approaching deadline, a govt job, or a weekly digest) <strong>plus 1 daily personalized
                    greeting each morning that uses the user's name</strong> to nudge them to open the app. All as
                    in-app 🔔 + mobile push. The same scheme is never repeated within a week.
                    <br /><br />
                    <strong>Send Now (below):</strong> instantly pushes one fresh personalized item to
                    <strong> every</strong> user. Unlimited — click as often as you like. It bypasses the daily
                    cap and never blocks the automatic sends. Each click delivers new content (dedup still applies).
                  </p>
                </div>
                <Button
                  onClick={handleRunPersonalized}
                  disabled={personalizedRunning}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 whitespace-nowrap"
                  data-testid="run-personalized-notifications-btn"
                >
                  {personalizedRunning ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Send to All Users Now</>
                  )}
                </Button>
              </div>

              {personalizedResult && (
                <div data-testid="personalized-result" className="mt-2">
                  <div className="flex items-center gap-2 text-green-700 mb-3">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Sweep complete — here's what happened:</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-800">{personalizedResult.users ?? 0}</p>
                      <p className="text-xs text-gray-600">Users scanned</p>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-indigo-600">{personalizedResult.notified ?? 0}</p>
                      <p className="text-xs text-gray-600">Notified</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{personalizedResult.push_devices ?? 0}</p>
                      <p className="text-xs text-gray-600">Push delivered</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">{personalizedResult.skipped_cap ?? 0}</p>
                      <p className="text-xs text-gray-600">Skipped (daily cap)</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-500">{personalizedResult.no_content ?? 0}</p>
                      <p className="text-xs text-gray-600">No new content</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    <strong>Notified</strong> = in-app notifications created (users see them under the 🔔).
                    <strong> Push delivered</strong> = mobile devices reached via Firebase — this stays 0 in
                    preview (Firebase runs only in production) and until users register devices.
                  </p>
                </div>
              )}
            </Card>


            {/* Partner Link Health */}
            <Card className="p-6 bg-white border border-gray-200" data-testid="admin-link-health-card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-rose-600" />
                    Partner Link Health
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                    Checks every partner link in the "For You" tab and flags broken (404), unreachable or
                    erroring links so they never go stale. Runs automatically on the 1st of each month; click
                    to run it now. (Note: some partners use single-page apps that always return 200, so soft
                    404s can't always be detected — "unreachable" may also mean the site is just slow/blocking.)
                  </p>
                </div>
                <Button
                  onClick={handleRunLinkHealth}
                  disabled={linkHealthRunning}
                  className="bg-rose-600 hover:bg-rose-700 text-white px-6 py-2 whitespace-nowrap"
                  data-testid="run-link-health-btn"
                >
                  {linkHealthRunning ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" />Run Link Check</>
                  )}
                </Button>
              </div>

              {linkHealthReport && linkHealthReport.summary && (
                <div data-testid="link-health-result" className="mt-2">
                  <div className="flex items-center gap-2 text-gray-600 mb-3 text-xs">
                    <Clock className="w-4 h-4" />
                    Last checked: {linkHealthReport.created_at ? new Date(linkHealthReport.created_at).toLocaleString() : '—'}
                    {linkHealthReport.source === 'monthly' && <Badge className="ml-1 bg-gray-100 text-gray-600">auto</Badge>}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-800">{linkHealthReport.summary.total ?? 0}</p>
                      <p className="text-xs text-gray-600">Total links</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{linkHealthReport.summary.ok ?? 0}</p>
                      <p className="text-xs text-gray-600">OK</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">{linkHealthReport.summary.broken ?? 0}</p>
                      <p className="text-xs text-gray-600">Broken (404)</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">{(linkHealthReport.summary.unreachable ?? 0) + (linkHealthReport.summary.error ?? 0)}</p>
                      <p className="text-xs text-gray-600">Unreachable</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-500">{linkHealthReport.summary.blocked ?? 0}</p>
                      <p className="text-xs text-gray-600">Blocked (bot)</p>
                    </div>
                  </div>

                  {(linkHealthReport.flagged || []).length === 0 ? (
                    <div className="flex items-center gap-2 text-green-700 text-sm p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5" /> All partner links are reachable. Nothing to fix.
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="link-health-flagged">
                      {(linkHealthReport.flagged || []).map((r, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{r.name} <span className="text-gray-400 font-normal">· {r.service}</span></p>
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">{r.url}</a>
                          </div>
                          <Badge className={r.state === 'broken' ? 'bg-red-100 text-red-700 shrink-0' : 'bg-amber-100 text-amber-700 shrink-0'}>
                            {r.state === 'broken' ? `${r.status} broken` : (r.state === 'unreachable' ? 'unreachable' : `${r.status || ''} ${r.state}`)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>


            {/* Notification History */}
            <Card className="p-6 bg-white border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                Recent Notifications
              </h3>
              
              {notificationHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No notifications sent yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notificationHistory.map((notification, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notification.type === 'auto_news' 
                          ? 'bg-blue-100 text-blue-600' 
                          : 'bg-purple-100 text-purple-600'
                      }`}>
                        {notification.type === 'auto_news' ? (
                          <Newspaper className="w-4 h-4" />
                        ) : (
                          <Bell className="w-4 h-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{notification.title}</p>
                        <p className="text-sm text-gray-600 truncate">{notification.body}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {notification.type === 'auto_news' ? 'Auto' : notification.type === 'broadcast' ? 'Broadcast' : 'Manual'}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {notification.sent_at ? new Date(notification.sent_at).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Info Card */}
            <Card className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
              <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Mobile App Integration
              </h3>
              <ul className="space-y-2 text-sm text-purple-800">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5">•</span>
                  <strong>Auto News Notifications:</strong> Sent every 6 hours with latest scheme news
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5">•</span>
                  <strong>Manual Notifications:</strong> Use the composer above to send instant alerts to all users
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5">•</span>
                  <strong>Device Registration:</strong> Users are automatically registered when they install the mobile app
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5">•</span>
                  <strong>Firebase FCM:</strong> All notifications are sent via Firebase Cloud Messaging
                </li>
              </ul>
            </Card>
          </div>
        )}

        {/* View Reset Tab */}
        {activeTab === 'view-reset' && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Premium View Reset Management</h2>
                <p className="text-gray-600">
                  Monitor and manage 45-day view reset cycles for premium scheme users
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={loadViewResetData}
                  variant="outline"
                  className="text-gray-600"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button
                  onClick={resetAllExpiredViews}
                  disabled={resettingViews}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {resettingViews ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Clock className="w-4 h-4 mr-2" />
                  )}
                  Reset All Expired
                </Button>
              </div>
            </div>

            {/* Summary Stats */}
            {viewResetData && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card className="p-4 bg-white border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{viewResetData.total_users_with_views}</p>
                      <p className="text-sm text-gray-600">Users with Views</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-red-50 border border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-700">{viewResetData.users_needing_reset}</p>
                      <p className="text-sm text-red-600">Need Reset Now</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-700">{viewResetData.users_reset_within_7_days}</p>
                      <p className="text-sm text-amber-600">Reset in 7 Days</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-green-50 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{viewResetData.reset_period_days}</p>
                      <p className="text-sm text-green-600">Day Reset Cycle</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Users List */}
            {viewResetData?.users?.length > 0 ? (
              <Card className="bg-white border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">#</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">User</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Phone</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Subscription</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Views Used</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Schemes Viewed</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Days Until Reset</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {viewResetData.users.map((user, index) => (
                        <tr 
                          key={user.user_id} 
                          className={`hover:bg-gray-50 ${
                            user.needs_reset ? 'bg-red-50' : 
                            user.days_until_reset <= 7 ? 'bg-amber-50' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                              user.needs_reset ? 'bg-red-600' :
                              user.days_until_reset <= 7 ? 'bg-amber-600' :
                              user.days_until_reset <= 14 ? 'bg-yellow-500' : 'bg-green-600'
                            }`}>
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{user.user_name}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{user.user_phone}</td>
                          <td className="px-4 py-3">
                            <Badge className={`text-xs ${
                              user.subscription_status === 'subscribed' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {user.subscription_status === 'subscribed' ? 'Premium' : 'Free'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center font-semibold text-gray-900">{user.total_views_used}</td>
                          <td className="px-4 py-3 text-center text-gray-600">{user.schemes_viewed}</td>
                          <td className="px-4 py-3 text-center">
                            {user.needs_reset ? (
                              <Badge className="bg-red-600 text-white animate-pulse">
                                EXPIRED
                              </Badge>
                            ) : (
                              <div className={`font-bold ${
                                user.days_until_reset <= 7 ? 'text-amber-600' :
                                user.days_until_reset <= 14 ? 'text-yellow-600' : 'text-green-600'
                              }`}>
                                {user.days_until_reset} days
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              onClick={() => resetUserViews(user.user_id, user.user_name)}
                              disabled={resettingViews}
                              size="sm"
                              variant={user.needs_reset ? "default" : "outline"}
                              className={user.needs_reset ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                            >
                              Reset
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="p-12 text-center bg-white border border-gray-200">
                <Eye className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No View Data Yet</h3>
                <p className="text-gray-500">No users have viewed premium schemes yet.</p>
              </Card>
            )}

            {/* Info Card */}
            <Card className="mt-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
              <h3 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                View Reset Information
              </h3>
              <ul className="space-y-2 text-sm text-amber-800">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  Premium scheme views reset automatically every <strong>45 days</strong>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  Users can view up to <strong>2 premium schemes</strong> before requiring subscription
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  Automated reset runs daily at <strong>2:00 AM UTC</strong>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  Users are sorted by <strong>days until reset</strong> (least first for priority)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-600"></span> Red</span> = Expired, 
                  <span className="inline-flex items-center gap-1 ml-2"><span className="w-3 h-3 rounded-full bg-amber-600"></span> Amber</span> = Within 7 days, 
                  <span className="inline-flex items-center gap-1 ml-2"><span className="w-3 h-3 rounded-full bg-green-600"></span> Green</span> = More than 14 days
                </li>
              </ul>
            </Card>
          </div>
        )}

        {/* Tab Analytics Tab */}
        {activeTab === 'tab-analytics' && (
          <TabAnalyticsPanel />
        )}

        {/* Discovery Queue Tab */}
        {activeTab === 'discovery' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Scheme Discovery Queue</h2>
              <Button onClick={loadDiscoveryData} disabled={discoveryLoading} variant="outline" data-testid="refresh-discovery">
                <RefreshCw className={`w-4 h-4 mr-2 ${discoveryLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Daily Sweep panel */}
            <Card className="p-5 mb-6 bg-white border border-gray-200" data-testid="daily-sweep-card">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-5 h-5 text-orange-600" />
                    <h3 className="font-semibold text-gray-900">Active Discovery Sweep</h3>
                    <Badge className="bg-green-100 text-green-700 text-xs">Scheduled every 24h</Badge>
                    {sweepStatus?.currently_running > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                        <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />
                        Running now
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    Claude Haiku autonomously enumerates currently-active government schemes across Central + AP + TG + KA. New finds land in the pending-review queue below.
                  </p>
                  {sweepStatus?.next_run_estimate && (
                    <p className="text-xs text-gray-500 mt-2">
                      Next scheduled run: <strong>{new Date(sweepStatus.next_run_estimate).toLocaleString()}</strong>
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0 items-center">
                  <select
                    value={sweepRegion}
                    onChange={(e) => setSweepRegion(e.target.value)}
                    className="h-10 px-3 border border-gray-300 rounded-md text-sm bg-white"
                    data-testid="sweep-region-select"
                    disabled={sweepRunning}
                  >
                    <option value="">All 4 regions</option>
                    <option value="Central">Central</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Karnataka">Karnataka</option>
                  </select>
                  <Button
                    onClick={runSweepNow}
                    disabled={sweepRunning}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="sweep-run-now-btn"
                  >
                    {sweepRunning ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                    ) : (
                      <><Zap className="w-4 h-4 mr-2" /> Run Now</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Per-region latest */}
              {sweepStatus && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {['Central', 'Andhra Pradesh', 'Telangana', 'Karnataka'].map((region) => {
                    const r = sweepStatus.latest_by_region[region];
                    return (
                      <div
                        key={region}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                        data-testid={`sweep-region-${region.toLowerCase().replace(/ /g, '-')}`}
                      >
                        <div className="text-xs text-gray-500 mb-1">{region}</div>
                        {r ? (
                          <>
                            <div className="text-lg font-bold text-orange-600">{r.new_schemes_found}</div>
                            <div className="text-[11px] text-gray-500">new, {r.extracted} extracted</div>
                            <div className="text-[11px] text-gray-400 mt-1">
                              {new Date(r.finished_at).toLocaleDateString()}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-400 italic">No runs yet</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recent run history */}
              {sweepStatus?.recent_runs?.length > 0 && (
                <div className="border-t border-gray-200 pt-3">
                  <div className="text-xs font-medium text-gray-700 mb-2">Recent runs</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="sweep-history-table">
                      <thead>
                        <tr className="text-gray-500 border-b border-gray-200">
                          <th className="text-left py-1.5 pr-3">When</th>
                          <th className="text-left py-1.5 pr-3">Region</th>
                          <th className="text-left py-1.5 pr-3">Trigger</th>
                          <th className="text-left py-1.5 pr-3">Status</th>
                          <th className="text-right py-1.5">New / Extracted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sweepStatus.recent_runs.slice(0, 10).map((run) => (
                          <tr key={run.run_id} className="border-b border-gray-100">
                            <td className="py-1.5 pr-3 text-gray-700">
                              {new Date(run.started_at).toLocaleString()}
                            </td>
                            <td className="py-1.5 pr-3 text-gray-700">{run.region}</td>
                            <td className="py-1.5 pr-3">
                              <Badge className={run.triggered_by === 'scheduler' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'} variant="outline">
                                {run.triggered_by}
                              </Badge>
                            </td>
                            <td className="py-1.5 pr-3">
                              <Badge className={
                                run.status === 'success' ? 'bg-green-100 text-green-700' :
                                run.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }>
                                {run.status}
                              </Badge>
                            </td>
                            <td className="py-1.5 text-right font-medium text-gray-900">
                              {run.new_schemes_found} / {run.extracted}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card>

            {/* SEO: Slug Repair */}
            <Card className="p-5 mb-6 bg-white border border-gray-200" data-testid="seo-slug-repair-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold text-gray-900">SEO: Repair Scheme & Job Slugs</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Regenerates clean, SEO-friendly URL slugs (<code className="text-xs bg-gray-100 px-1 rounded">pm-kisan-samman-nidhi</code>) for any row still holding a UUID, underscore, or internal <code className="text-xs bg-gray-100 px-1 rounded">kms_disc_*</code> ID. Old URLs keep resolving via the existing redirect fallback. Run Dry-Run first to preview the changes.
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    onClick={() => runSlugBackfill(false)}
                    disabled={slugBackfill.loading}
                    variant="outline"
                    data-testid="slug-backfill-dryrun-btn"
                  >
                    {slugBackfill.loading && slugBackfill.mode === 'DRY_RUN' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Dry-Run
                  </Button>
                  <Button
                    onClick={() => runSlugBackfill(true)}
                    disabled={slugBackfill.loading}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="slug-backfill-apply-btn"
                  >
                    {slugBackfill.loading && slugBackfill.mode === 'APPLY' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Apply
                  </Button>
                </div>
              </div>

              {slugBackfill.result && (
                <div className="mt-4 border-t border-gray-200 pt-4" data-testid="slug-backfill-result">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={slugBackfill.result.mode === 'APPLY' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                      {slugBackfill.result.mode === 'APPLY' ? 'Applied' : 'Dry-Run Preview'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {['schemes', 'government_jobs'].map((key) => {
                      const r = slugBackfill.result[key];
                      const label = key === 'schemes' ? 'Schemes' : 'Government Jobs';
                      return (
                        <div key={key} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <h4 className="font-semibold text-gray-900 text-sm mb-2">{label}</h4>
                          <div className="grid grid-cols-2 gap-y-1 text-xs">
                            <span className="text-gray-600">Total rows:</span>
                            <span className="font-medium text-gray-900 text-right">{r.total_rows}</span>
                            <span className="text-gray-600">Already clean:</span>
                            <span className="font-medium text-green-700 text-right">{r.already_clean}</span>
                            <span className="text-gray-600">Needed backfill:</span>
                            <span className={`font-medium text-right ${r.needed_backfill > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                              {r.needed_backfill}
                            </span>
                            {slugBackfill.result.mode === 'APPLY' ? (
                              <>
                                <span className="text-gray-600">Rows updated:</span>
                                <span className="font-semibold text-indigo-700 text-right">{r.rows_updated}</span>
                              </>
                            ) : (
                              <>
                                <span className="text-gray-600">Would update:</span>
                                <span className="font-semibold text-indigo-700 text-right">{r.would_update}</span>
                              </>
                            )}
                          </div>
                          {r.sample_transforms && r.sample_transforms.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-[11px] text-gray-500 mb-1">Sample transforms:</div>
                              <div className="space-y-1">
                                {r.sample_transforms.slice(0, 5).map((t, i) => (
                                  <div key={i} className="text-[11px] font-mono break-all leading-snug">
                                    <span className="text-red-600">{t.old_slug}</span>
                                    <span className="text-gray-400"> → </span>
                                    <span className="text-green-700">{t.new_slug}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {slugBackfill.result.mode === 'APPLY' && (
                    <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                      <strong>Next:</strong> Purge Cloudflare cache for <code>/api/sitemap.xml</code>, <code>/api/sitemap-schemes.xml</code>, <code>/api/sitemap-jobs.xml</code>, then resubmit sitemaps in Google Search Console.
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Discovery Stats */}
            {discoveryStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard icon={Database} title="Total Discovered" value={discoveryStats.total_discovered} color="text-orange-600" subtitle="By KMS Smart AI" />
                <StatCard icon={Clock} title="Pending Review" value={discoveryStats.pending_verification} color="text-yellow-600" subtitle="Need admin action" />
                <StatCard icon={CheckCircle} title="Verified" value={discoveryStats.verified} color="text-green-600" subtitle="Admin approved" />
                <StatCard icon={Zap} title="This Week" value={discoveryStats.discovered_this_week} color="text-blue-600" subtitle="New discoveries" />
              </div>
            )}

            {/* Discovered Schemes List */}
            {discoveredSchemes.length === 0 ? (
              <Card className="p-8 bg-white border border-gray-200 text-center">
                <Zap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Discovered Schemes Yet</h3>
                <p className="text-sm text-gray-500">KMS Smart will auto-discover new schemes as users run analyses.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {discoveredSchemes.map((scheme) => (
                  <Card key={scheme.id} className="p-4 bg-white border border-gray-200" data-testid={`discovery-card-${scheme.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{scheme.name}</h3>
                          <Badge className={
                            scheme.verification_status === 'verified' ? 'bg-green-100 text-green-700' :
                            scheme.verification_status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {scheme.verification_status}
                          </Badge>
                          <Badge className={
                            scheme.confidence === 'high' ? 'bg-green-100 text-green-700' :
                            scheme.confidence === 'medium' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }>
                            {scheme.confidence} confidence
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{scheme.description || scheme.shortDescription}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          <span>Sector: <strong>{scheme.sector}</strong></span>
                          <span>Ministry: <strong>{scheme.ministry || 'N/A'}</strong></span>
                          <span>Discovered: <strong>{scheme.times_discovered}x</strong></span>
                          <span>From: <strong>{scheme.discovery_profile_type || 'N/A'}</strong></span>
                          <span>Date: <strong>{scheme.created_at ? new Date(scheme.created_at).toLocaleDateString() : 'N/A'}</strong></span>
                        </div>
                      </div>
                      {scheme.verification_status === 'pending' && (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleDiscoveryAction(scheme.id, 'verify')} data-testid={`verify-${scheme.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Verify
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDiscoveryAction(scheme.id, 'reject')} data-testid={`reject-${scheme.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                          <Button size="sm" variant="outline" className="text-gray-600" onClick={() => handleDiscoveryAction(scheme.id, 'mark-duplicate')} data-testid={`duplicate-${scheme.id}`}>
                            Mark Duplicate
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Government Jobs Management Tab */}
        {activeTab === 'govt-jobs' && (
          <div data-testid="admin-jobs-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Government Jobs Management</h2>
              <div className="flex gap-2">
                <Button onClick={() => setShowAddJob(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-job-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> Add Job
                </Button>
                <Button onClick={handleRefreshJobs} disabled={jobsRefreshing} variant="outline" data-testid="refresh-jobs-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${jobsRefreshing ? 'animate-spin' : ''}`} />
                  {jobsRefreshing ? 'Fetching...' : 'Refresh Sources'}
                </Button>
              </div>
            </div>

            {/* Jobs Stats */}
            {jobsStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard icon={Briefcase} title="Active Jobs" value={jobsStats.total_active} color="text-blue-600" subtitle="Currently listed" />
                <StatCard icon={Database} title="Total Jobs" value={jobsStats.total} color="text-gray-600" subtitle="All time" />
                <StatCard icon={XCircle} title="Inactive" value={jobsStats.total_inactive} color="text-red-600" subtitle="Expired/removed" />
                <StatCard icon={CheckCircle} title="Sources OK" value={jobsStats.last_fetch?.sources_success?.length || 0} color="text-green-600" subtitle={`of ${(jobsStats.last_fetch?.sources_success?.length || 0) + (jobsStats.last_fetch?.sources_failed?.length || 0)} total`} />
              </div>
            )}

            {/* Category Breakdown */}
            {jobsStats?.category_breakdown && Object.keys(jobsStats.category_breakdown).length > 0 && (
              <Card className="p-4 mb-6 bg-white border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-3">Category Breakdown</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(jobsStats.category_breakdown).map(([cat, count]) => (
                    <Badge key={cat} className="bg-blue-50 text-blue-700 text-xs px-3 py-1">{cat}: {count}</Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Last Fetch Info */}
            {jobsStats?.last_fetch && (
              <Card className="p-4 mb-6 bg-gray-50 border border-gray-200" data-testid="fetch-report">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm">Last Fetch Report</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs mb-3">
                  <div><span className="text-gray-500">Total Fetched:</span> <strong>{jobsStats.last_fetch.total_fetched}</strong></div>
                  <div><span className="text-gray-500">New Added:</span> <strong className="text-green-700">{jobsStats.last_fetch.new_added}</strong></div>
                  <div><span className="text-gray-500">Seed Jobs:</span> <strong>{jobsStats.last_fetch.seed_jobs_count || 0}</strong></div>
                  <div><span className="text-gray-500">Active After:</span> <strong className="text-blue-700">{jobsStats.last_fetch.total_active_after || 'N/A'}</strong></div>
                  <div><span className="text-gray-500">Completed:</span> <strong>{jobsStats.last_fetch.completed_at ? new Date(jobsStats.last_fetch.completed_at).toLocaleString() : 'N/A'}</strong></div>
                </div>
                {/* Detailed source report */}
                {jobsStats.last_fetch.source_details?.length > 0 && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <p className="text-xs font-medium text-gray-600 mb-2">Source-by-Source Report:</p>
                    <div className="max-h-60 overflow-y-auto space-y-1">
                      {jobsStats.last_fetch.source_details.map((d, i) => (
                        <div key={i} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${d.status === 'success' ? 'bg-green-50' : d.status === 'empty' ? 'bg-yellow-50' : 'bg-red-50'}`}>
                          <span className="font-medium text-gray-700">{d.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{d.count} jobs</span>
                            <span className={`font-semibold ${d.status === 'success' ? 'text-green-600' : d.status === 'empty' ? 'text-yellow-600' : 'text-red-600'}`}>
                              {d.status === 'success' ? 'OK' : d.status === 'empty' ? 'EMPTY' : 'FAIL'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Legacy failed sources display */}
                {!jobsStats.last_fetch.source_details && jobsStats.last_fetch.sources_failed?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-600 font-medium">Failed Sources: {jobsStats.last_fetch.sources_failed.map(f => typeof f === 'string' ? f : f.name).join(', ')}</p>
                  </div>
                )}
              </Card>
            )}

            {/* Add Job Modal */}
            {showAddJob && (
              <Card className="p-6 mb-6 bg-white border-2 border-blue-200 shadow-lg" data-testid="add-job-form">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Add New Job</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input placeholder="Job Title *" value={newJob.title} onChange={e => setNewJob({...newJob, title: e.target.value})} data-testid="new-job-title" />
                  <Input placeholder="Organisation" value={newJob.organisation} onChange={e => setNewJob({...newJob, organisation: e.target.value})} data-testid="new-job-org" />
                  <Input placeholder="Department" value={newJob.department} onChange={e => setNewJob({...newJob, department: e.target.value})} />
                  <select className="px-3 py-2 border rounded-md text-sm" value={newJob.category} onChange={e => setNewJob({...newJob, category: e.target.value})}>
                    {['Administrative and Civil Services', 'Banking and Finance', 'Railway', 'Teaching and Education', 'Defence and Paramilitary', 'Police and Law', 'Technology and IT', 'Medical and Health', 'Engineering and Technical', 'Clerical and Support', 'Agriculture', 'Post Office', 'Science and Research', 'Forest and Environment', 'Social Welfare', 'Sports Quota'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select className="px-3 py-2 border rounded-md text-sm" value={newJob.qualification} onChange={e => setNewJob({...newJob, qualification: e.target.value})}>
                    {['Various', '8th', '10th', '12th', 'Diploma/ITI', 'Graduate', 'Post Graduate'].map(q => <option key={q} value={q}>{q}</option>)}
                  </select>
                  <Input placeholder="Location" value={newJob.location} onChange={e => setNewJob({...newJob, location: e.target.value})} />
                  <Input placeholder="Vacancies" type="number" value={newJob.vacancies} onChange={e => setNewJob({...newJob, vacancies: e.target.value})} />
                  <Input placeholder="Salary Min" type="number" value={newJob.salary_min} onChange={e => setNewJob({...newJob, salary_min: e.target.value})} />
                  <Input placeholder="Salary Max" type="number" value={newJob.salary_max} onChange={e => setNewJob({...newJob, salary_max: e.target.value})} />
                  <Input placeholder="Last Date" type="date" value={newJob.last_date} onChange={e => setNewJob({...newJob, last_date: e.target.value})} data-testid="new-job-date" />
                  <Input placeholder="Apply URL" value={newJob.apply_url} onChange={e => setNewJob({...newJob, apply_url: e.target.value})} />
                  <Input placeholder="Notification PDF URL" value={newJob.notification_url} onChange={e => setNewJob({...newJob, notification_url: e.target.value})} />
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleCreateJob} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-new-job-btn">Create Job</Button>
                  <Button variant="outline" onClick={() => setShowAddJob(false)}>Cancel</Button>
                </div>
              </Card>
            )}

            {/* Jobs Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search jobs..."
                  value={adminJobsSearch}
                  onChange={e => { setAdminJobsSearch(e.target.value); loadAdminJobs(1, e.target.value); }}
                  className="pl-10 h-10 text-sm"
                  data-testid="admin-jobs-search"
                />
              </div>
            </div>

            {/* Jobs List */}
            <div className="space-y-2">
              {adminJobs.map((job) => (
                <Card key={job.job_id} className="p-3 bg-white border border-gray-200 hover:shadow-md transition" data-testid={`admin-job-${job.job_id}`}>
                  {editingJob === job.job_id ? (
                    <AdminJobEditRow job={job} onSave={(updates) => handleUpdateJob(job.job_id, updates)} onCancel={() => setEditingJob(null)} />
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-800 truncate">{job.title}</h4>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                          <span>{job.organisation}</span>
                          <Badge className={`text-[10px] ${job.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{job.is_active ? 'Active' : 'Inactive'}</Badge>
                          <span>{job.category}</span>
                          {job.vacancies && <span>{job.vacancies} vacancies</span>}
                          <span>Source: {job.source}</span>
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditingJob(job.job_id)} data-testid={`edit-job-${job.job_id}`}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteJob(job.job_id)} data-testid={`delete-job-${job.job_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
              {adminJobs.length === 0 && !jobsLoading && (
                <Card className="p-8 text-center bg-white border border-gray-200">
                  <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No jobs found</p>
                </Card>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-3">Showing {adminJobs.length} of {adminJobsTotal} jobs</p>
          </div>
        )}

        {/* Money Hub Management Tab */}
        {activeTab === 'money-hub' && (
          <div data-testid="admin-money-hub-panel">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Money Hub — Data Refresh</h2>
                <p className="text-sm text-gray-500 mt-1">Ask Claude to fetch the latest rates from official sources and overwrite each collection.</p>
              </div>
              <a
                href="/money-hub"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-md inline-flex items-center gap-1"
              >
                Open public Money Hub →
              </a>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-5 leading-relaxed">
              <strong>How this works:</strong> Each "Refresh" button sends a prompt to Claude (via Emergent LLM key) requesting the most current values for that collection, then overwrites the MongoDB collection on success. If Claude's response can't be parsed, the existing data is kept. Verify each refresh by visiting the public Money Hub page afterwards.
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                { key: 'fd_rates',            label: 'Bank FD Rates',          desc: '19 banks (Govt, Private, SFB, NBFC)' },
                { key: 'govt_bonds',          label: 'Government Bonds',       desc: 'SGB, RBI Floating Rate, 54EC NHAI/REC' },
                { key: 'post_office_schemes', label: 'Post Office Schemes',    desc: 'PPF, NSC, SCSS, SSY, KVP, MIS, MSSC, RD, FD' },
                { key: 'gold_options',        label: 'Gold Options',           desc: 'SGB / ETF / MF / Physical' },
                { key: 'mf_platforms',        label: 'Mutual Fund Platforms',  desc: 'Groww, Zerodha Coin, Paytm Money' },
                { key: 'retirement_schemes',  label: 'Retirement Schemes',     desc: 'EPF, NPS, APY' },
              ].map((c) => {
                const result = moneyHubResults[c.key];
                const isLoading = moneyHubLoading[c.key];
                return (
                  <Card key={c.key} className="p-4 bg-white border border-gray-200" data-testid={`money-hub-card-${c.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900">{c.label}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{c.desc}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => refreshMoneyHubCollection(c.key)}
                        disabled={isLoading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs"
                        data-testid={`money-hub-refresh-${c.key}`}
                      >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                        {isLoading ? 'Refreshing…' : 'Refresh'}
                      </Button>
                    </div>
                    {result && (
                      <div className={`mt-3 text-xs rounded-md p-2 ${result.ok ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' : 'bg-rose-50 text-rose-900 border border-rose-200'}`}>
                        {result.ok ? (
                          <><CheckCircle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> Updated — {result.rows} rows written.</>
                        ) : (
                          <><XCircle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> {result.error}</>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Consent History Modal */}
      {consentHistoryOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="consent-history-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConsentHistoryOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Training-data consent history</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {consentHistoryUser?.name} · {consentHistoryUser?.phone} · current state:{' '}
                  <span className={consentHistoryUser?.training_data_opted_in === false ? 'text-amber-700 font-medium' : 'text-emerald-700 font-medium'}>
                    {consentHistoryUser?.training_data_opted_in === false ? 'Opted out' : 'Opted in'}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setConsentHistoryOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
                data-testid="close-consent-history-btn"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {consentHistoryLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : consentHistoryItems.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No consent change events recorded for this user yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs text-gray-500">
                      <th className="text-left py-2">When (UTC)</th>
                      <th className="text-left py-2">Action</th>
                      <th className="text-left py-2">Terms</th>
                      <th className="text-right py-2">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consentHistoryItems.map(item => (
                      <tr key={item.log_id} className="border-b border-gray-100" data-testid={`consent-row-${item.log_id}`}>
                        <td className="py-2 text-gray-700 text-xs">{new Date(item.created_at).toLocaleString()}</td>
                        <td className="py-2">
                          {item.opted_in ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Opted in</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">Opted out</Badge>
                          )}
                        </td>
                        <td className="py-2 text-gray-500 text-xs">{item.terms_version}</td>
                        <td className="py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-green-600 text-green-700 hover:bg-green-50"
                            onClick={() => {
                              const token = localStorage.getItem('token');
                              window.open(`${API_URL}/api/auth/admin/consent-pdf/${item.log_id}?token=${token}`, '_blank');
                            }}
                            data-testid={`download-consent-pdf-${item.log_id}`}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Job Edit Row Component
const AdminJobEditRow = ({ job, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: job.title || '',
    organisation: job.organisation || '',
    category: job.category || 'Central',
    qualification: job.qualification || 'Various',
    location: job.location || 'All India',
    vacancies: job.vacancies || '',
    last_date: job.last_date || '',
    is_active: job.is_active ?? true,
  });
  return (
    <div className="space-y-2" data-testid={`edit-form-${job.job_id}`}>
      <Input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="text-sm h-8" />
      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="Organisation" value={form.organisation} onChange={e => setForm({...form, organisation: e.target.value})} className="text-sm h-8" />
        <Input placeholder="Location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="text-sm h-8" />
        <Input placeholder="Last Date" type="date" value={form.last_date} onChange={e => setForm({...form, last_date: e.target.value})} className="text-sm h-8" />
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} /> Active</label>
        <Button size="sm" className="bg-blue-600 text-white h-7 px-3 text-xs" onClick={() => onSave(form)}>Save</Button>
        <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

// User Engagement Row Component with Expandable Tab Breakdown
const UserEngagementRow = ({ user, index, formatTime }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <>
      <tr 
        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${expanded ? 'bg-cyan-50' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="py-3 px-2">
          <button className="p-1 hover:bg-gray-200 rounded transition">
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </td>
        <td className="py-3 px-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
            index === 0 ? 'bg-yellow-100 text-yellow-700' :
            index === 1 ? 'bg-gray-100 text-gray-700' :
            index === 2 ? 'bg-orange-100 text-orange-700' :
            'bg-gray-50 text-gray-600'
          }`}>
            {index + 1}
          </span>
        </td>
        <td className="py-3 px-2">
          <span className="text-sm font-medium text-gray-900">{user.name}</span>
        </td>
        <td className="py-3 px-2">
          <span className="text-sm text-cyan-600 font-medium">{formatTime(user.total_time)}</span>
        </td>
        <td className="py-3 px-2">
          <span className="text-sm text-gray-600">{user.visit_count}</span>
        </td>
        <td className="py-3 px-2">
          <span className="text-sm text-cyan-600 font-medium cursor-pointer hover:underline">
            {user.tabs_visited} tabs
          </span>
        </td>
        <td className="py-3 px-2">
          <span className="text-sm text-gray-600">{formatTime(user.avg_time_per_visit)}</span>
        </td>
      </tr>
      {expanded && user.tab_breakdown && user.tab_breakdown.length > 0 && (
        <tr>
          <td colSpan="7" className="p-0">
            <div className="bg-gradient-to-r from-cyan-50 to-white px-4 py-3 border-b border-cyan-100">
              <div className="ml-8 space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Tab Activity for {user.name}
                </p>
                {user.tab_breakdown.map((tab, tabIndex) => (
                  <div key={tab.tab_name} className="flex items-center gap-3">
                    <div className="flex items-center gap-2 min-w-[20px]">
                      {tabIndex === user.tab_breakdown.length - 1 ? (
                        <span className="text-gray-400">└──</span>
                      ) : (
                        <span className="text-gray-400">├──</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between flex-1 bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-100">
                      <span className="text-sm font-medium text-gray-700">{tab.tab_name}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-500">{tab.visit_count} visits</span>
                        <span className="text-sm font-semibold text-cyan-600">{formatTime(tab.time_spent)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Tab Analytics Panel Component
const TabAnalyticsPanel = () => {
  const [tabStats, setTabStats] = useState(null);
  const [topUsers, setTopUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(7);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTabAnalytics();
  }, [selectedDays]);

  const fetchTabAnalytics = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/tab-stats?days=${selectedDays}`),
        fetch(`${API_URL}/api/analytics/top-users-by-engagement?limit=10&days=${selectedDays}`)
      ]);
      
      const statsData = await statsRes.json();
      const usersData = await usersRes.json();
      
      setTabStats(statsData);
      setTopUsers(usersData);
    } catch (error) {
      console.error('Failed to fetch tab analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Tab-Level Analytics</h2>
          <p className="text-sm text-gray-600">Track user engagement across different sections</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Period:</span>
          <select
            value={selectedDays}
            onChange={(e) => setSelectedDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button
            onClick={fetchTabAnalytics}
            className="px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm hover:bg-cyan-700 transition"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {tabStats?.success && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 bg-gradient-to-br from-cyan-50 to-white border-cyan-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Clock className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Time</p>
                <p className="text-2xl font-bold text-cyan-700">{formatTime(tabStats.total_time_seconds)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-green-50 to-white border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Visits</p>
                <p className="text-2xl font-bold text-green-700">{tabStats.total_visits?.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-50 to-white border-purple-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Tabs Tracked</p>
                <p className="text-2xl font-bold text-purple-700">{tabStats.tabs?.length || 0}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tab Breakdown */}
      {tabStats?.success && tabStats.tabs?.length > 0 && (
        <Card className="p-6 bg-white border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Spent by Tab</h3>
          <div className="space-y-4">
            {tabStats.tabs.map((tab, index) => (
              <div key={tab.tab_name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{tab.tab_name}</span>
                    <span className="text-xs text-gray-500">({tab.visit_count} visits)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{formatTime(tab.total_time)}</span>
                    <span className="text-sm font-medium text-green-600">{tab.percentage}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-500 bg-green-500"
                    style={{
                      width: `${tab.percentage}%`
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Unique Users: {tab.unique_users}</span>
                  <span>Avg Time: {formatTime(tab.avg_time)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Top Engaged Users */}
      {topUsers?.success && topUsers.top_users?.length > 0 && (
        <Card className="p-6 bg-white border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Engaged Users</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600 w-8"></th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">#</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">User</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Total Time</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Visits</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Tabs</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Avg/Visit</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.top_users.map((user, index) => (
                  <UserEngagementRow 
                    key={user.user_id} 
                    user={user} 
                    index={index} 
                    formatTime={formatTime} 
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* No Data Message */}
      {tabStats?.success && (!tabStats.tabs || tabStats.tabs.length === 0) && (
        <Card className="p-8 bg-white border border-gray-200 text-center">
          <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No Tab Data Yet</h3>
          <p className="text-sm text-gray-500">Tab analytics will appear once users start browsing the site.</p>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
